// Angular
import { BehaviorSubject, Observable, Subject, debounceTime, delay, forkJoin, map, share, takeUntil } from 'rxjs';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// Auth
import { AuthService } from '../../../auth';

// Libraries
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { XYChart } from '@amcharts/amcharts4/charts';
import { SpritePointerTypeEvent } from '@amcharts/amcharts4/.internal/core/SpriteEvents';
import { SpriteMouseTouchEvent, SpritePointEvent } from '@amcharts/amcharts4/core';

import { takeWhile } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';

import { GridsterItem } from 'angular-gridster2';
import { MatDrawer } from '@angular/material/sidenav';

import { Select, Store } from '@ngxs/store';

// Models
import { AppCleanup, ChartInterval, ChartIntervals, ColorPickerChoice, ColorPickerType, Colors, DrawerModel, LineStyleOption, LineStyles, LineWeights, UserModel } from '../../../../shared/models';
import { JobCommentDto } from '../../models/job-comment-dto';
import { ChartSeries, GetJobDataPayload, Job, JobData, JobStatus, JobUpdate, UserUnitPeriodChannelValue } from '../../models';
import { Channel, LineStyle } from '../../../channel/models';
import { JobDataIDBResponse } from '../../../../shared/workers/job-db';
import { JobDatumFlat, JobDatumFlatPropCollection } from '../../models/job-datum-flat-model';
import { JobReturnCache } from '../../models/job-return-cache.model';
import { JobCommentDefaultGroupDto } from '../../../comment/models/job-comment-default-group-dto';
import { JobCommentDefaultDto } from '../../../comment/models/job-comment-default-dto';
import { UserUnitPeriodChannelDto } from '../../../channel/models/user-unit-period-channel-dto';
import { UnitPeriodChannelDto } from '../../../channel/models/unit-period-channel-dto.model';
import { JobReturnData } from '../../models/job-return-data.model';
import { CalibrationCloseDto } from '../../models/calibration-close-dto';
import { JobTypeChannelDto } from '../daily-report/models/job-type-channel-dto';
import { JobCommentDefaultResponseAllDto } from '../../../comment/models/job-comment-default-response-all-dto';

// Services
import { JobService } from '../../services/job.service';
import { JobCommentService } from '../../../comment/services/job-comment.service';
import { AppSettingsService, CoreService, DCGService } from '../../../../shared/services';
import { JobDataService } from '../../../../shared/workers/job-data.service';
import { DashboardService } from '../dashboard/services/dashboard.service';
import { TimezoneService } from '../../services/timezone.service';
import { GlobalService } from '../../../../shared/services';
import { ChannelService } from '../../../channel/services';
import { SubheaderService } from '../../../../shared/components/subheader/services/subheader.service';
import { SpinnerService } from '../../../../shared/components/spinner/services/spinner.service';

// Components
import { OffsetChannelDialogComponent } from '../job-overview/dialogs/offset-channel-dialog/offset-channel-dialog.component';
import { ColorPickerComponent, ColorPickerData } from '../../../../shared/components';
import { EditUserChannelsDialogComponent } from '../job-overview/dialogs/edit-user-channels-dialog/edit-user-channels-dialog.component';
import { CalibrationDialogComponent } from '../job-overview/dialogs/calibration-dialog/calibration-dialog.component';
import { CommentAddDialogComponent } from '../daily-report/dialogs/comment-add-dialog/comment-add-dialog.component';

// Enums
import { GetJobDataOperation } from '../../models/get-job-data-operation.enum';
import { StatusType, ShiftType } from '../../../../shared/enums';
import { Roles } from '../../../auth/models/roles.enum';
import { ChangeType } from '../../enums/change-type.enum';

// Hub
import { JobHub } from '../../services/job.hub';

// Helpers
import { DateHelper, UtilityHelper } from '../../../../shared/helpers';
import { getTime } from 'date-fns';

// Pipes
import { MilitaryTimePipe, MilitaryTimeDatePipe } from '../../../../shared/pipes';

// Store
import { StorageState } from '../../../../shared/state/storage.state';
import { CommentDataState } from '../../../comment/state/comment.state';
import { CommentDataActions } from '../../../comment/state/comment.action';
import { DashboardState } from '../dashboard/state/dashboard.state';
import { AuthState } from '../../../auth/state/auth.state';

@Component({
	selector: 'job-chart-old',
	templateUrl: './job-chart-old.component.html',
	styleUrls: ['job-chart-old.component.scss'],
	providers: [MilitaryTimePipe, MilitaryTimeDatePipe],
})
export class JobChartOldComponent extends AppCleanup implements OnInit, OnChanges, OnDestroy {
	@Select(AuthState.isLoggedIn) isLoggedIn$: Observable<boolean>;
	@Select(StorageState.commentDrawerSettings) commentDrawerSettings$: Observable<DrawerModel>;
	@Select(CommentDataState.isLoaded) commentDataLoaded$: Observable<boolean>;
	@Select(CommentDataState.commentData) commentData$: Observable<JobCommentDefaultResponseAllDto>;
	commentDrawerSettings: DrawerModel;
	@Select(DashboardState.currentDashboardGridsterItems) currentDashboardGridsterItems$: Observable<GridsterItem[]>;
	currentDashboardGridsterItems: GridsterItem[];

	isWidget = false;
	channels: Channel[] = [];
	/** The gridster item information of the Job Chart. Will be null if not in Dashboard*/
	@Input() gridsterItem: GridsterItem;
	/** Sets the default Chart time span in minutes for graph */
	@Input() chartTimeSpanInMinutes: number;
	/** Job Key */
	@Input() jobKey: string;
	/** Widget is in an edit state */
	@Input() isEditable: boolean = false;
	/** Show header on chart */
	@Input() showHeaderInfo: boolean = true;
	/** Show channel values above chart */
	@Input() showChannelValues: boolean = true;
	/** Show icon to download data */
	@Input() showDataDownload: boolean = true;
	/** Allow Comments to be visible */
	@Input() allowCommentVisibility: boolean = true;
	/** Show icon for viewing Job Links */
	@Input() showLinkIcon: boolean = true;
	/** Distinguish for when JobChart is in Dashboard mode */
	@Input() mode: string;
	@Input() cursorBehavior: any;
	/** Triggers event to indicate widget has closed */
	@Output() onDelete: EventEmitter<boolean> = new EventEmitter();
	@Output() onCopy: EventEmitter<any> = new EventEmitter();
	@Output() onRotate: EventEmitter<any> = new EventEmitter();

	@Input() data: ITimeline2Data[];

	@ViewChild('drawer', { static: false }) drawer: MatDrawer;
	@ViewChild('chartDiv') chartDiv: ElementRef;

	resizeTimeout: NodeJS.Timeout;
	chartState$ = new BehaviorSubject<ChartState | null>(null);
	datumsWaitingOnChannelValues: JobDatumFlat[] = [];

	@HostListener('window:resize', ['$event'])
	onResize() {
		clearTimeout(this.resizeTimeout);

		this.resizeTimeout = setTimeout(() => {
			this._chartSizeChangeAction();
		}, 500);

		this.autoHideCommentLog();
	}

	@HostListener('window:custom-event', ['$event'])
	showCommentDialog(event: CustomEvent) {
		this.openCommentAddDialog(this.chartState$.value, null, 'job-chart', event.detail);
	}

	/**Defaults to 10 minutes for Local Viewer and 240 minutes for Portal */
	readonly defaultChartTimeSpanInMinutes: number = this.isViewer ? 10 : 240;
	isMobile: boolean = false;
	_presetColors: Array<string> = [...Colors.ChartDefaults];
	/** Is the JobChart component taller than it is wide */
	vertical = false;
	fabTimeout: NodeJS.Timeout;
	_chartTimeRange: IChartTimeRange = null;
	_selectDateAction = '';
	_selectedJobComment: JobCommentDto;
	_initializedChart = false;
	_blockDataApiCall: boolean = true;
	_lastMinZoomed: number;
	_lastMaxZoomed: number;
	_forceRefresh = false;
	_jobResponse: JobDataIDBResponse;
	_jobReturnCaches: JobReturnCache[];
	_bufferedFlattenedData: JobDatumFlatPropCollection = {};
	_paddingDivisor = 12; //The higher the number, the less padding there will be for live data coming into the chart
	_jobInitialized = false;
	readyForChart$ = new Subject<void>();

	/**Public Variables */
	job: Job;
	timeInterval: number = 5000;

	/**If true, the Job is viewable to the Public through a snapshot key. If false, you must be logged in to see the job*/
	isPublic: boolean;
	displayDataTable: boolean = false;
	zoomed: boolean = false;
	shifted: boolean = false;
	endRightShift: boolean = false;
	endLeftShift: boolean = false;
	userJobChannelValuesToShow: UserUnitPeriodChannelValue[] = [];
	jobElapsedTimeDisplayText: string;
	timeDiffMins: number;
	initializingData: boolean = true;
	reloadingData: boolean = true;
	isEditing = false;
	channelClass: string = '';
	hasInitialized: boolean = false;
	intervals: Array<ChartInterval> = [...ChartIntervals.List];
	lineStyleOptions: Array<LineStyleOption> = [...LineStyles.List];
	lineWeightOptions: Array<number> = [...LineWeights.List];
	showTooltips: boolean = true;
	disableDragging = true;
	displayFab = false;
	editingFromFab = false;
	userJobChannelValues: UserUnitPeriodChannelValue[];
	lastUpdatedDate: Date;
	chartTimeRangeDisplayText: string;
	isScreenLarge = false;
	jobInitialized = false;
	lastDataPerUnitPeriod: JobData[] = [];
	jobId: number;
	url: string;
	isAdmin: boolean;
	sortedJobComments: JobCommentDto[] = [];
	jobCommentDefaultGroups: JobCommentDefaultGroupDto[] = [];
	jobCommentDefaults: JobCommentDefaultDto[] = [];
	_bufferedLiveData: JobData[] = [];
	unitPeriodError = false;
	isEditMode: boolean = false;

	constructor(
		public dashboardService: DashboardService,
		private militaryTimePipe: MilitaryTimePipe,
		private authService: AuthService,
		private subheaderService: SubheaderService,
		private timezoneService: TimezoneService,
		private globalService: GlobalService,
		private dialog: MatDialog,
		public jobService: JobService,
		private jobCommentService: JobCommentService,
		private jobDataService: JobDataService,
		private coreService: CoreService,
		private settingsService: AppSettingsService,
		private changeDetectorRef: ChangeDetectorRef,
		private jobHub: JobHub,
		public route: ActivatedRoute,
		private dcgService: DCGService,
		private router: Router,
		private zone: NgZone,
		private channelService: ChannelService,
		private spinnerService: SpinnerService,
		private store: Store
	) {
		super();
	}

	ngOnInit() {
		this.commentDrawerSettings$.pipe(takeUntil(this.destroy)).subscribe((commentDrawerSettings) => {
			if (commentDrawerSettings) {
				this.commentDrawerSettings = commentDrawerSettings;
			}
		});

		this.isLoggedIn$.pipe(takeUntil(this.destroy)).subscribe((isLoggedIn) => {
			// No need for comment groups to initialize on public links
			if (isLoggedIn) {
				this.commentDataLoaded$.pipe(takeUntil(this.destroy)).subscribe((isLoaded) => {
					if (!isLoaded) {
						this.store.dispatch(new CommentDataActions.GetAll({ showSpinner: false, showToast: false }));
					}
				});
			}
		});

		this.settingsService
			.getSettings()
			.pipe(
				delay(1), // wait for view to initialize so chartDiv is available.
				takeUntil(this.destroy)
			)
			.subscribe((settings) => {
				am4core.addLicense(settings.amchartsKey);
				this.readyForChart$.next();
				this.readyForChart$.complete();
			});

		// Determines if the device is a mobile device
		this.globalService.isMobile.pipe(takeUntil(this.destroy)).subscribe((isMobile) => {
			this.isMobile = isMobile;
		});

		// Sets whether the comments are visible or not on the service side
		// where the visibility is managed.
		this.jobService.allowCommentVisibility = this.allowCommentVisibility;

		this.readyForChart$.pipe(takeUntil(this.destroy)).subscribe(() => {
			this._initializesAngularComponents();
		});

		this.timezoneService
			.onTimezoneChanged()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result && result.changed === true) {
					this.chartState$.value.chart.dateFormatter.timezoneOffset = result.offset * -60 || this.timezoneService.getCurrentTimeZone() * -60;
				}
			});

		this.channelService.channelsReady$.pipe(takeUntil(this.destroy)).subscribe((channels) => {
			this.channels = channels;
		});

		this.jobHub.dataReceived$.pipe(takeUntil(this.destroy)).subscribe((data: Array<JobData>) => {
			if (data && data.length > 0) {
				let refresh = false;
				let userUnitPeriodChannels = this.findUserUnitPeriodChannels();
				data.forEach((data) => {
					if (data.k === this.job?.key && data.c && data.c.length > 0) {
						data.c.forEach((ch) => {
							let found = userUnitPeriodChannels.find((channel) => channel?.unitPeriodChannel?.channel?.id === ch.i);
							if (!found) {
								refresh = true;
							}
						});
					}
				});
			}
		});

		this.currentDashboardGridsterItems$.pipe(takeUntil(this.destroy)).subscribe((currentDashboardGridsterItems) => {
			if (currentDashboardGridsterItems) {
				this.currentDashboardGridsterItems = UtilityHelper.deepCopy(currentDashboardGridsterItems);
			}
		});

		this.dashboardService.gridsterConfig$.pipe(takeUntil(this.destroy)).subscribe((gridsterConfig) => {
			if (gridsterConfig) {
				this.isEditMode = gridsterConfig?.draggable?.enabled;
			}
		});
	}

	ngOnChanges() {
		this.vertical = this.gridsterItem.isVertical;
		if (this.gridsterItem) {
			this.isWidget = true;
		}
	}

	private autoHideCommentLog() {
		// this.isScreenLarge = window.innerWidth >= 1200;
		// if (!this.isScreenLarge) {
		// 	if (this.jobService.commentDrawer.opened) {
		// 		this.jobService.toggleCommentDrawer();
		// 		this.changeDetectorRef.markForCheck();
		// 	}
		// }
	}

	private _initializesAngularComponents() {
		this.chartTimeSpanInMinutes = this.chartTimeSpanInMinutes || this.defaultChartTimeSpanInMinutes;

		this.isPublic = this.route.snapshot.data && this.route.snapshot.data.hasOwnProperty('public') && this.route.snapshot.data.public;

		this.url = this.router.url;

		this.subheaderService.disabled$.next(false);

		this.authService
			.checkAccess([Roles.TenantAdmin, Roles.SuperAdmin, Roles.Admin], () => {
				return true;
			})
			.pipe(takeUntil(this.destroy))
			.subscribe((isAdmin) => {
				this.isAdmin = isAdmin;
			});

		this.jobKey = this.jobKey || this.route.snapshot.paramMap.get('jobId');

		this.jobService.userChannelsChanged$.pipe(takeUntil(this.destroy)).subscribe((ucc) => {
			if (ucc) {
				this.findUserUnitPeriodChannels().forEach((_) => {
					this._modifyAxisDisplay(this.chartState$.value, _.unitPeriodChannel.unitPeriodId, _.unitPeriodChannel.channelId);
				});
				this.changeDetectorRef.markForCheck();
			}
		});

		if (!this.isPublic) {
			this.jobHub.connectionEstablished$.pipe(takeUntil(this.destroy)).subscribe((result) => {
				if (result) {
					this.jobHub.startSendingData([this.jobKey]);
				}
			});

			this._initJob();
			this._setIntervalForLiveData();
		} else {
			this.jobService
				.getJobIdFromKey(this.jobKey)
				.pipe(takeUntil(this.destroy))
				.subscribe((jobId) => {
					if (jobId) {
						this.jobId = jobId;

						//Needed for public links since Parent does not subscribe
						//this.jobHub.startSendingData([String(this.jobId)]);
						//this.jobHub.registerOnServerEvents();

						this.jobHub.connectionEstablished$.pipe(takeUntil(this.destroy)).subscribe((result) => {
							if (result) {
								this.jobHub.startSendingData([String(this.jobId)]);
							}
						});

						this._initJob();
						this._setIntervalForLiveData();
					}
				});
		}
	}

	private _setIntervalForLiveData(interval: number = 1000) {
		let lastDeepCopyOfLastJobData: JobData[] = [];

		setInterval(() => {
			if (lastDeepCopyOfLastJobData.length === 0 || lastDeepCopyOfLastJobData.length !== this.lastDataPerUnitPeriod.length) lastDeepCopyOfLastJobData = UtilityHelper.deepCopy(this.lastDataPerUnitPeriod);

			lastDeepCopyOfLastJobData.forEach((jobData) => {
				let lastJobDataForUnitPeriod = this.lastDataPerUnitPeriod.find((ljd) => ljd.u === jobData.u);

				if (lastJobDataForUnitPeriod && jobData.d < lastJobDataForUnitPeriod.d) {
					let deepCopyOfLastJobData = UtilityHelper.deepCopy(lastJobDataForUnitPeriod);

					if (deepCopyOfLastJobData) {
						deepCopyOfLastJobData.c.forEach((c) => {
							let unitPeriodId = this.userJobChannelValuesToShow.find((ujc) => ujc.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId === deepCopyOfLastJobData.u)?.userUnitPeriodChannel?.unitPeriodChannel?.unitPeriodId;
							let findIndex = this.userJobChannelValuesToShow.findIndex((ujc) => ujc.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId === unitPeriodId && ujc.userUnitPeriodChannel.unitPeriodChannel.channelId === c.i);

							if (findIndex >= 0) {
								this.userJobChannelValuesToShow[findIndex].value = c.v;
							}
						});

						this.lastUpdatedDate = deepCopyOfLastJobData.d;
					}

					let findIndex = lastDeepCopyOfLastJobData.findIndex((ldc) => ldc.u === jobData.u);

					if (findIndex >= 0) {
						lastDeepCopyOfLastJobData[findIndex] = deepCopyOfLastJobData;
					}

					this.changeDetectorRef.markForCheck();
				}
			});

			if (this._bufferedLiveData.length > 0 && this.chartExists(this.chartState$.value) && !this.initializingData && !this.reloadingData) {
				// transform the data
				let newData = this.jobService.applyOffsetsToDataPoints(this.jobKey, this._bufferedLiveData);
				newData = this.jobService.applyPrecisionToDataPoints(this.jobKey, newData);

				// update most recent unit period data
				this.lastDataPerUnitPeriod = this.updateDataByUnitPeriodId(this.lastDataPerUnitPeriod, newData);

				newData = this.jobService.filterOutOfRangePoints(this.jobKey, newData);

				this._addDataToChart(this.chartState$.value, newData);
			}

			this._bufferedLiveData = [];
		}, interval);
	}

	private _addDataToChart(chartState: ChartState, data: Array<JobData>) {
		if (data.length === 0) return;

		data.sort((a, b) => {
			return new Date(a.d).getTime() - new Date(b.d).getTime();
		});

		const chartStartDate = new Date(this._chartTimeRange.start);
		let newDatums: Array<JobDatumFlat> = [];

		let firstDataUnixMs = data[0].d.getTime();
		let lastDataUnixMs = data[data.length - 1].d.getTime();

		let intervalMs = 1000;
		for (let currentUnixMs = firstDataUnixMs; currentUnixMs <= lastDataUnixMs; currentUnixMs += intervalMs) {
			let dataWithinCurrentInterval = data.filter((d) => currentUnixMs <= d.d.getTime() && d.d.getTime() < currentUnixMs + intervalMs);

			if (this.chartTimeSpanInMinutes === 0) {
				dataWithinCurrentInterval.forEach((d) => {
					this.appendDataToBuffer(this._bufferedFlattenedData, this._flattenData(d));
				}, this);

				if (this.getDataTimeSpan(this._bufferedFlattenedData) >= this.timeInterval) {
					const averagedData = this.getAveragedData(this._bufferedFlattenedData);

					newDatums.push(averagedData);
					this._bufferedFlattenedData = {};
				}
			} else {
				let dataWithinCurrentIntervalAfterChartStartDate = dataWithinCurrentInterval.filter((d) => d.d >= chartStartDate);

				dataWithinCurrentIntervalAfterChartStartDate.forEach((d) => {
					this.appendDataToBuffer(this._bufferedFlattenedData, this._flattenData(d));
				}, this);

				if (this.chartTimeSpanInMinutes <= 10 || this.getDataTimeSpan(this._bufferedFlattenedData) >= this.timeInterval) {
					const averagedData = this.getAveragedData(this._bufferedFlattenedData);

					newDatums.push(averagedData);
					this._bufferedFlattenedData = {};
				}
			}
		}

		const gapThreshold = this.isViewer ? 1100 : 2000;

		// Grabbing the last 3, so we have multiple to choose from within the threshold.
		// one might have the channels we're looking for during interpolation, others might not.
		let lastChartDatums: JobDatumFlat[] = chartState.chart.data
			.slice(-3)
			.map((di) => di as JobDatumFlat)
			//Not sure right now if we guarantee that the chart's dataContext items are sorted. Doing it again here. :)
			.sort((a, b) => a.date - b.date);

		let lastChartDatum = lastChartDatums[lastChartDatums.length - 1];

		const minJavaScriptDate = new Date(-8640000000000000);
		let lastChartDatumDate = lastChartDatum?.date ?? minJavaScriptDate.getTime();

		// only keep the new datums that occur after the last chart data.  TODO: but keep it if has channels we're looking for?
		newDatums = newDatums.filter((d) => lastChartDatumDate < d.date);
		this.datumsWaitingOnChannelValues = this.datumsWaitingOnChannelValues.filter((d) => lastChartDatumDate < d.date);

		newDatums = [...this.datumsWaitingOnChannelValues, ...newDatums];
		const datumsWithinThreshold = [...lastChartDatums, ...newDatums].sort((a, b) => a.date - b.date);

		if (newDatums.length > 0) {
			if (newDatums.length + chartState.chart.data.length >= 10000) {
				this.updateChartTimeSpanAndGetNewSamplePoints(chartState);
			} else {
				const unitPeriodIds = this.job.unitPeriods.map((up) => up.id);

				const channels = unitPeriodIds.flatMap((unitPeriodId) =>
					this.jobService.findUnitPeriodChannels(this.job.key, unitPeriodId).map((upc: UnitPeriodChannelDto) => ({
						unitPeriodId,
						channelId: upc.channelId,
						keyString: `${unitPeriodId}_${upc.channelId}`,
						precision: upc.channel?.precision,
					}))
				);

				for (let i = 0; i < newDatums.length; i++) {
					const current = newDatums[i];

					for (const { unitPeriodId, channelId, keyString, precision } of channels) {
						// if a datum is missing a channelKey as a property, then we're going to interpolate its value based on its neighbors
						if (typeof current[keyString] !== 'number') {
							let datums = datumsWithinThreshold.filter((d) => d != current);

							// find the first date that occurs before the current date
							let d1 = datums
								.slice()
								.reverse()
								.find((d) => d.date <= current.date && typeof d[keyString] === 'number');
							let d2 = datums.find((d) => current.date < d.date && typeof d[keyString] === 'number');

							if (d1 && d2) {
								let v1 = d1[keyString];
								let v2 = d2[keyString];

								let t1: number = d1.date;
								let t2: number = d2.date;

								// interpolate the values between the two points to find the value it would have been at the current time
								let newValue: number = v1 + ((v2 - v1) * (current.date - t1)) / (t2 - t1);

								current[keyString] = +newValue.toFixed(precision);
							} else {
								if (!this.datumsWaitingOnChannelValues.includes(current)) this.datumsWaitingOnChannelValues.push(current);
							}
						}
					}
				}

				//let datumLengthBefore = this.datumsWaitingOnChannelValues.length;
				this.datumsWaitingOnChannelValues = this.datumsWaitingOnChannelValues.filter((d) => Date.now() - gapThreshold < d.date);
				newDatums = newDatums.filter((d) => !this.datumsWaitingOnChannelValues.includes(d));

				if (newDatums.length == 0) return;

				let dataCountBeforeChartStart = chartState.chart.data.filter((d) => d.date < chartStartDate.getTime()).length;
				chartState.chart.addData(newDatums, dataCountBeforeChartStart);

				//For loading new data points, do not alter the gap	since the scale has not changed
				//Start time is the last points end time minus the chart interval in milliseconds.
				//If chartInterval is 0 (all), then chart will compress instead of scroll
				const dateAxis = this._getDateAxis(chartState);

				//Don't shift if shifting is disabled
				if ((!this.shifted && !this.zoomed) || this.endRightShift) {
					//If chartInterval is zoomed and not defined, use interval of
					let intervalCalc = this.chartTimeSpanInMinutes;
					if (this.chartTimeSpanInMinutes == null) {
						let diff = dateAxis.maxZoomed - dateAxis.minZoomed;
						intervalCalc = diff;
					} else {
						intervalCalc *= 60000;
					}

					//End Time is Last Point, Start Time is Last Point minus the chart interval
					let startTime = 0;
					let endTime = 0;
					if (this.chartTimeSpanInMinutes == 0) {
						endTime = newDatums[newDatums.length - 1].date;
						startTime = dateAxis.minZoomed;
					} else {
						endTime = newDatums[newDatums.length - 1].date;
						startTime = endTime - intervalCalc;
					}

					let padding = (endTime - startTime) / this._paddingDivisor;

					// this._setDateMinMax(chartState, startTime, endTime + padding, false); // Adds padding at the end
					this._setDateMinMax(chartState, startTime, endTime, false);
				}
			}
		}
	}

	private chartExists(value: ChartState | null) {
		return value && value.chart && !value.chart.isDisposed();
	}

	private updateDataByUnitPeriodId(currentData: JobData[], newData: Array<JobData>): JobData[] {
		const updatedData = [...currentData];

		newData.forEach((newItem) => {
			let existingItemIndex = updatedData.findIndex((existingItem) => existingItem.u === newItem.u);

			if (existingItemIndex < 0) {
				updatedData.push(newItem);
			} else {
				updatedData[existingItemIndex] = newItem;
			}
		});

		return updatedData;
	}

	private appendDataToBuffer(bufferedFlattenedData: JobDatumFlatPropCollection, flattenedData: JobDatumFlat) {
		for (const prop in flattenedData) {
			if (!bufferedFlattenedData[prop]) bufferedFlattenedData[prop] = [];
			bufferedFlattenedData[prop].push(flattenedData[prop]);
		}
	}

	private getAveragedData(data: JobDatumFlatPropCollection) {
		const averagedData: JobDatumFlat = {};

		for (const avgProp in data) {
			averagedData[avgProp] = this._averageValue(data[avgProp]);
		}
		return averagedData;
	}

	private getDataTimeSpan(data: JobDatumFlatPropCollection): number | null {
		if (data['date'] === undefined || data['date'] === null) return null;
		if (data['date'].length <= 1) return null;

		return data['date'][data['date'].length - 1] - data['date'][0];
	}

	/**Initializes all Job Data and fires the jobChange$ observable. This initializes the Job data for all child components as well.
	 * If the Job is ready or has changed, the jobChange$ observable will refresh the chart with a new API call.
	 */
	private _initJob() {
		// Make a very basic call to the Job Table
		if (this.isPublic && !this._jobInitialized) {
			this.jobService.initPublicJob(this.jobKey);
		}

		let waitingForJob = true;

		this.jobService
			.getJobStream(String(this.jobId))
			.pipe(takeUntil(this.destroy))
			.subscribe((jobUpdate: JobUpdate) => {
				if (jobUpdate) {
					// Because I don't know why
					if (jobUpdate.changeType === ChangeType.INITIAL && this.isPublic && this._jobInitialized) return;

					if (jobUpdate.changeType === ChangeType.INITIAL || waitingForJob) {
						this._jobInitialized = true;

						waitingForJob = false;

						this.jobInitialized = true;

						this.job = this.jobService.findJob(jobUpdate.job.key);

						this._mergeChannels();

						this._sortChannelsForMobile();

						this._bindData();

						let payload: GetJobDataPayload;
						if (this.job.jobStatus.isCompleted) {
							payload = {
								key: this.route.snapshot.paramMap.get('jobId'),
								unitPeriodIds: this.job.unitPeriods.map((unitPeriod) => unitPeriod.id),
								startDate: new Date(this.job.end.getTime() - this.chartTimeSpanInMinutes * 60000),
								endDate: new Date(this.job.end.getTime()),
								maxDate: this.job.end,
								zoomed: false,
								shifted: false,
								shift: null,
								minutes: this.chartTimeSpanInMinutes,
								operation: GetJobDataOperation.ChangeInterval,
							};
						} else {
							payload = {
								key: this.route.snapshot.paramMap.get('jobId'),
								unitPeriodIds: this.job.unitPeriods.map((unitPeriod) => unitPeriod.id),
								startDate: new Date(new Date().getTime() - this.chartTimeSpanInMinutes * 60000),
								endDate: null,
								maxDate: this.job.end,
								zoomed: false,
								shifted: false,
								shift: null,
								minutes: this.chartTimeSpanInMinutes,
								operation: GetJobDataOperation.ChangeInterval,
							};
						}

						this._takeSampleAndUpdateChart(this.chartState$.value, payload);
					} else if (jobUpdate.changeType === ChangeType.SYNC_CHANGE) {
						this.job.lastSyncRequest = jobUpdate.job.lastSyncRequest;
					} else {
						this.job = this.jobService.findJob(jobUpdate.job.key);

						this.changeChartInterval(this.chartState$.value, this.chartTimeSpanInMinutes);

						this._mergeChannels();

						this._sortChannelsForMobile();

						this.filterJobCommentDefaults();

						let userUnitPeriodChannels = this.findUserUnitPeriodChannels();

						if (jobUpdate.changeType === ChangeType.USER_UNIT_PERIOD_CHANNEL) {
							//Need to now completely redraw/show axises since potentially could be 30+
							let userUnitPeriodChannelsToRemove = this._removeChannelSeries(this.chartState$.value, userUnitPeriodChannels);

							this._removeYAxises(this.chartState$.value, userUnitPeriodChannels);

							this._removeFromSeriesObject(this.chartState$.value, userUnitPeriodChannelsToRemove);

							this._addNewChannelsToGraph(this.chartState$.value, userUnitPeriodChannels);
						}

						//Modifying other values that may not have been added/deleted
						userUnitPeriodChannels.forEach((_) => {
							this._modifyAxisDisplay(this.chartState$.value, _.unitPeriodChannel.unitPeriodId, _.unitPeriodChannel.channelId);
						});

						if (this.chartState$.value) this._updateChartDataAndCommentSeries(this.chartState$.value, this.chartState$.value.chart.data);
					}
				}
			});

		// this.jobService.jobChange$.pipe(takeUntil(this.destroy)).subscribe((jobChange) => {
		// 	if (jobChange && (jobChange.job.key == this.jobKey || this.isPublic)) {
		// 		// Because I don't know why
		// 		if (jobChange.changeType === ChangeType.INITIAL && this.isPublic && this._jobInitialized) return;

		// 		if (jobChange.changeType === ChangeType.INITIAL || waitingForJob) {
		// 			this._jobInitialized = true;

		// 			waitingForJob = false;

		// 			this.jobInitialized = true;

		// 			this.job = this.jobService.findJob(jobChange.job.key);

		// 			this._mergeChannels();

		// 			this._sortChannelsForMobile();

		// 			this._bindData();

		// 			let payload: GetJobDataPayload;
		// 			if (this.job.jobStatus.isCompleted) {
		// 				payload = {
		// 					key: this.route.snapshot.paramMap.get('jobId'),
		// 					unitPeriodIds: this.job.unitPeriods.map((unitPeriod) => unitPeriod.id),
		// 					startDate: new Date(this.job.end.getTime() - this.chartTimeSpanInMinutes * 60000),
		// 					endDate: new Date(this.job.end.getTime()),
		// 					maxDate: this.job.end,
		// 					zoomed: false,
		// 					shifted: false,
		// 					shift: null,
		// 					minutes: this.chartTimeSpanInMinutes,
		// 					operation: GetJobDataOperation.ChangeInterval,
		// 				};
		// 			} else {
		// 				payload = {
		// 					key: this.route.snapshot.paramMap.get('jobId'),
		// 					unitPeriodIds: this.job.unitPeriods.map((unitPeriod) => unitPeriod.id),
		// 					startDate: new Date(new Date().getTime() - this.chartTimeSpanInMinutes * 60000),
		// 					endDate: null,
		// 					maxDate: this.job.end,
		// 					zoomed: false,
		// 					shifted: false,
		// 					shift: null,
		// 					minutes: this.chartTimeSpanInMinutes,
		// 					operation: GetJobDataOperation.ChangeInterval,
		// 				};
		// 			}

		// 			this._takeSampleAndUpdateChart(this.chartState$.value, payload);
		// 		} else if (jobChange.changeType === ChangeType.SYNC_CHANGE) {
		// 			this.job.lastSyncRequest = jobChange.job.lastSyncRequest;
		// 		} else {
		// 			this.job = this.jobService.findJob(jobChange.job.key);

		// 			this.changeChartInterval(this.chartState$.value, this.chartTimeSpanInMinutes);

		// 			this._mergeChannels();

		// 			this._sortChannelsForMobile();

		// 			this.filterJobCommentDefaults();

		// 			let userUnitPeriodChannels = this.findUserUnitPeriodChannels();

		// 			if (jobChange.changeType === ChangeType.USER_UNIT_PERIOD_CHANNEL) {
		// 				//Need to now completely redraw/show axises since potentially could be 30+
		// 				let userUnitPeriodChannelsToRemove = this._removeChannelSeries(this.chartState$.value, userUnitPeriodChannels);

		// 				this._removeYAxises(this.chartState$.value, userUnitPeriodChannels);

		// 				this._removeFromSeriesObject(this.chartState$.value, userUnitPeriodChannelsToRemove);

		// 				this._addNewChannelsToGraph(this.chartState$.value, userUnitPeriodChannels);
		// 			}

		// 			//Modifying other values that may not have been added/deleted
		// 			userUnitPeriodChannels.forEach((_) => {
		// 				this._modifyAxisDisplay(this.chartState$.value, _.unitPeriodChannel.unitPeriodId, _.unitPeriodChannel.channelId);
		// 			});

		// 			if (this.chartState$.value) this._updateChartDataAndCommentSeries(this.chartState$.value, this.chartState$.value.chart.data);
		// 		}
		// 	}
		// });
	}

	displayTimeZone(date: Date): Date {
		return this.timezoneService.timeZoneDate(date, this.job?.utcDateTimeOffset || this.timezoneService.getCurrentTimeZone());
	}

	_removeChannelSeries(chartState: ChartState, userUnitPeriodChannels: UserUnitPeriodChannelDto[]): UserUnitPeriodChannelDto[] {
		let userUnitPeriodChannelsToRemove: UserUnitPeriodChannelDto[] = [];
		let indexesToRemove: number[] = [];

		//Collecting indexes and then removing because we can't alter same series we are looping through.
		//Having to loop because .filter doesn't work
		for (let series of chartState.chart.series) {
			//If series has a channel still in it that shouldn't be shown, remove it
			let upId: Number;
			let chId: number;
			if (series.id.split('_')) {
				upId = Number(series.id.split('_')[0]);
				chId = Number(series.id.split('_')[1]);
			}
			let uupc = userUnitPeriodChannels.filter((uupc) => !uupc.showGraph).find((uupc) => uupc.unitPeriodChannel.unitPeriodId === upId && uupc.unitPeriodChannel.channelId === chId);

			if (uupc) {
				userUnitPeriodChannelsToRemove.push(uupc);
				let findIndex = chartState.chart.series.indexOf(series);
				indexesToRemove.push(findIndex);
			}
		}

		indexesToRemove.sort((a, b) => a - b);

		for (let i = indexesToRemove.length - 1; i >= 0; i--) {
			let indexToRemove = indexesToRemove[i];
			chartState.chart.series.removeIndex(indexToRemove);
		}

		return userUnitPeriodChannelsToRemove;
	}

	private _removeYAxises(chartState: ChartState, userUnitPeriodChannels: UserUnitPeriodChannelDto[]) {
		//Removing Axises because these weren't removed when the series data was removed.
		let axisIndexesToRemove: number[] = [];
		//Having to loop because .filter doesn't work
		for (let yAxis of chartState.chart.yAxes) {
			//If series has a point still in it that shouldn't be shown, remove it
			let uupc = userUnitPeriodChannels.filter((uupc) => !uupc.showGraph).find((uupc) => `axis-${uupc.unitPeriodChannel.unitPeriodId}_${uupc.unitPeriodChannel.channelId}` === yAxis.id);

			if (uupc) {
				let findIndex = chartState.chart.yAxes.indexOf(yAxis);
				axisIndexesToRemove.push(findIndex);
			}
		}

		axisIndexesToRemove.sort((a, b) => a - b);

		for (let i = axisIndexesToRemove.length - 1; i >= 0; i--) {
			let indexToRemove = axisIndexesToRemove[i];
			chartState.chart.yAxes.removeIndex(indexToRemove);
		}
	}

	private _removeFromSeriesObject(chartState: ChartState, userUnitPeriodChannelsToRemove: UserUnitPeriodChannelDto[]) {
		//Removing channel from series copy
		userUnitPeriodChannelsToRemove.forEach((uupc) => {
			if (Object.prototype.hasOwnProperty.call(chartState.series, `${uupc.unitPeriodChannel.unitPeriodId}_${uupc.unitPeriodChannel.channelId}`)) {
				delete chartState.series[`${uupc.unitPeriodChannel.unitPeriodId}_${uupc.unitPeriodChannel.channelId}`];
			}
		});
	}

	private _addNewChannelsToGraph(chartState: ChartState, userUnitPeriodChannels: UserUnitPeriodChannelDto[]) {
		// Adding new channels
		userUnitPeriodChannels.forEach((ujc) => {
			if (!Object.prototype.hasOwnProperty.call(chartState.series, `${ujc.unitPeriodChannel.unitPeriodId}_${ujc.unitPeriodChannel.channelId}`)) {
				let channelLabel = ujc.unitPeriodChannel.channel.label;

				if (ujc.unitPeriodChannel.channel.units) channelLabel += ` (${ujc.unitPeriodChannel.channel.units})`;

				if (chartState.chart.series.length < 11) {
					this._createAxisAndSeries(chartState, ujc.unitPeriodChannel.unitPeriodId, ujc.unitPeriodChannel.channel.id, channelLabel, ujc.colorHex, !ujc.showGraph, ujc.lineStyle, ujc.lineWeight, ujc);
				}
			}
		});
	}

	private _sortChannelsForMobile() {
		this.sortedJobComments = UtilityHelper.deepCopy(
			this.job.jobCommentReturn.jobComments
				.filter((jobComment) => !jobComment.isDeleted && (!jobComment.hideInPortal || this.isAdmin))
				.sort((a, b) => {
					return b.commentDate.getTime() - a.commentDate.getTime();
				})
		);

		this.sortedJobComments.forEach((sjc) => {
			sjc.jobCommentChannels = sjc.jobCommentChannels.filter((jobCommentChannel) => jobCommentChannel.value !== null);
		});
	}

	getHexColorForChannel(jobTypeChannel: JobTypeChannelDto): string {
		if (this.job && jobTypeChannel?.channelId) {
			let userJobChannel = this.findUserUnitPeriodChannels().find((ujc) => ujc.unitPeriodChannel.channelId === jobTypeChannel?.channelId);

			if (userJobChannel?.colorHex) {
				return userJobChannel.colorHex;
			}
		}

		return 'black';
	}

	/**
	 * Forms the proper payload for calling the API that retrieves a Sample of data from the server.
	 * @param payload Payload details that will determine what values are needed from the API Call
	 */
	private _takeSampleAndUpdateChart(chartState: ChartState, payload: GetJobDataPayload) {
		let offset: number = 0;
		if (payload.shift == ShiftType.LEFT) {
			offset = -payload.minutes * 30000;
		} else if (payload.shift == ShiftType.RIGHT) {
			offset = payload.minutes * 30000;
		} else if (payload.shift == ShiftType.BIGLEFT) {
			offset = -payload.minutes * 60000;
		} else if (payload.shift == ShiftType.RIGHTRIGHT) {
			offset = payload.minutes * 60000;
		}

		let startDate: number;
		let endDate: number;

		let rightShiftEnded = false;

		if (payload.minutes === null) {
			startDate = payload.startDate.getTime();
			endDate = payload.endDate.getTime();
		} else if (payload.minutes === 0) {
			startDate = this.job.start ? this.job.start.getTime() : this.job.unitPeriods[0]?.dateStarted.getTime();
			endDate = this.job.end ? this.job.end.getTime() : null;
			rightShiftEnded = !this.job.end;
		} else if (offset) {
			startDate = payload.startDate ? payload.startDate.getTime() + offset : this.job.start ? this.job.start.getTime() : this.job.unitPeriods[0]?.dateStarted.getTime();
			endDate = startDate + payload.minutes * 60000;
		} else if (payload.minutes > 0) {
			endDate = payload.endDate ? payload.endDate.getTime() : this.job.end ? this.job.end.getTime() : null;
			startDate = endDate ? endDate - payload.minutes * 60000 : new Date().getTime() - payload.minutes * 60000;
			rightShiftEnded = !this.job.end;
		}

		var now = Date.now();
		if (endDate >= now) {
			let minuteDiff = endDate - startDate;
			endDate = null;
			startDate = now - minuteDiff;
			rightShiftEnded = true;
		}

		this.reloadingData = true;

		if (this.job.unitPeriods.length > 0) {
			forkJoin(this.getTasksForJobUnitPeriods(payload, startDate, endDate))
				.pipe(this.takeUntilChartDisposed(chartState))
				.subscribe((x: JobReturnCache[]) => {
					this._jobReturnCaches = x;

					if (this._jobReturnCaches.length > 0) {
						if (this._jobReturnCaches[0]?.data.length > 1) {
							let unixData = this._jobReturnCaches[0].data.map((d) => d.unix);

							if (!endDate) endDate = Math.max(...unixData) * 1000;

							this.timeInterval = this._jobReturnCaches[0].data[1].unix - this._jobReturnCaches[0].data[0].unix;
						} else {
							this.timeInterval = 1000;
						}

						this._jobResponse = {
							rightShiftEnded: rightShiftEnded || (this.job.end && endDate >= this.job.end.getTime()) || (!this.job.end && endDate >= now),
							leftShiftEnded: startDate <= (this.job.start ? this.job.start.getTime() : this.job.unitPeriods[0]?.dateStarted.getTime()),
							startDate: startDate,
							endDate: endDate || now,
						};
					}

					this._drawChart(chartState);
				});
		} else {
			if (this.initializingData) {
				this.initializingData = false;
				this.unitPeriodError = true;
			}
		}
	}

	private getTasksForJobUnitPeriods(payload: GetJobDataPayload, startDate: number, endDate: number) {
		let tasksObservables: Observable<JobReturnCache>[] = [];

		payload.unitPeriodIds.forEach((upid) => {
			tasksObservables.push(!this.isPublic ? this.jobDataService.getJobDataSample(this.job.key, upid, startDate, endDate) : this.jobDataService.getPublicJobDataSample(payload.key, upid, startDate, endDate));
		});

		return tasksObservables;
	}

	/**
	 * For public job's, the User Job Channels will be the same as Job Channels since there is no concept of a User.
	 * This method also determines which Channels to show in the Channel Display based on the User Job Channels.
	 */
	private _mergeChannels() {
		let tempCopyOfChannelValues = UtilityHelper.deepCopy(this.userJobChannelValues);
		this.userJobChannelValues = [];

		this.findUserUnitPeriodChannels().forEach((uupc) => {
			//Getting currentValue of channel so that channel display does not blank out on Job Change
			let currentValue = null;

			if (tempCopyOfChannelValues?.length > 0) {
				currentValue = tempCopyOfChannelValues.find((tc) => tc.userUnitPeriodChannel?.id === uupc.id)?.value;
			}

			let userJobChannelValue: UserUnitPeriodChannelValue = {
				userUnitPeriodChannel: uupc,
				value: currentValue,
			};
			this.userJobChannelValues.push(userJobChannelValue);
		});

		this.userJobChannelValues.sort((a, b) => {
			return a.userUnitPeriodChannel.seq - b.userUnitPeriodChannel.seq;
		});

		this.userJobChannelValuesToShow = this.userJobChannelValues.filter((userJobChannelValue) => userJobChannelValue.userUnitPeriodChannel.display).slice(0, 12);
		this._getChannelClass();
	}

	/**
	 * Redraws the access for a Channel, based on the values of the User Job Channel
	 * @param channelId The Id of the Channel that the Axis will be redrawn for
	 */
	private _modifyAxisDisplay(chartState: ChartState, unitPeriodId: number, channelId: number) {
		if (Object.prototype.hasOwnProperty.call(chartState?.series, `${unitPeriodId}_${channelId}`)) {
			let userChannel = this.findUserUnitPeriodChannels().find((ujc) => ujc.unitPeriodChannel.unitPeriodId === unitPeriodId && ujc.unitPeriodChannel.channelId === channelId);

			if (userChannel) {
				this.changeChannelColor(chartState, userChannel.unitPeriodChannel.unitPeriodId, userChannel.unitPeriodChannel.channelId, userChannel.colorHex);
				this.changeLineWeight(chartState, userChannel.unitPeriodChannel.unitPeriodId, userChannel.unitPeriodChannel.channelId, userChannel.lineWeight);
				this.changeLineStyle(chartState, userChannel.unitPeriodChannel.unitPeriodId, userChannel.unitPeriodChannel.channelId, userChannel.lineStyle);
				this._changeAxisRange(
					chartState,
					userChannel.unitPeriodChannel.unitPeriodId,
					userChannel.unitPeriodChannel.channelId,
					userChannel.minY == null ? NaN : parseInt(String(userChannel.minY)),
					userChannel.maxY == null ? NaN : parseInt(String(userChannel.maxY))
				);
				this._hideUnusedAxises(chartState);
			}
		}
	}

	/**
	 * Assigns data to the chart and forms the Comment series
	 * @param jobDatums The flattened Job Data to assign to the chart
	 */
	private _updateChartDataAndCommentSeries(chartState: ChartState, jobDatums: JobDatumFlat[]) {
		if (this._jobResponse) {
			chartState.chart.data = jobDatums.filter((jobDatum) => !jobDatum['0_0']);

			if (chartState.series['0_0']) {
				let jobComments = this.job.jobCommentReturn.jobComments.filter((jobComment) => !jobComment.isDeleted && (!jobComment.hideInPortal || this.isAdmin));

				let segmentDiff = (this._jobResponse.endDate - this._jobResponse.startDate) / 50;

				let datums: JobDatumFlat[] = [];

				let currentTime = this._jobResponse.startDate;

				while (segmentDiff > 0 && currentTime <= this._jobResponse.endDate) {
					let datumSegment = jobComments.filter((jobComment) => jobComment.commentDate.getTime() >= currentTime && jobComment.commentDate.getTime() < currentTime + segmentDiff);

					if (datumSegment.length > 0) {
						//let averageDate = this._averageValue(datumSegment.map(ds => ds.commentDate.getTime()));
						let datum = { date: currentTime } as JobDatumFlat;
						datum['0_0'] = 0.05;
						datum.id = datumSegment[0].id;

						datum.comment = `<div class="comment-tooltip">`;
						for (let i = 0; i < datumSegment.length; i++) {
							datum.comment += `<div onclick="window.dispatchEvent(new CustomEvent('custom-event', {'detail': ${datumSegment[i].id}}));"><a href="javascript:;" >${this.militaryTimePipe.transform(this.displayTimeZone(datumSegment[i].commentDate))}: ${datumSegment[i].mappedComment} </a></div>`;
						}
						datum.comment += '</div>';

						datum.bullet = '../../../../assets/custom-icons/mode_comment-24px.svg';
						datum.color = am4core.color('black');

						// Without this process, the comment will have null channels values on the date/time point in which the comment is applied to
						// This then causes a tiny gap on every line where a comment has occured. This below will interpolate 2 points to find an estimated point value at the comment's date/time.
						let findClosestIndexedPoint = chartState.chart.data.findIndex((data) => data.date >= datum.date);

						if (findClosestIndexedPoint > 0) {
							let point1 = chartState.chart.data[findClosestIndexedPoint - 1];
							let point2 = chartState.chart.data[findClosestIndexedPoint];

							for (var prop in chartState.series) {
								if (prop !== '0_0' && prop !== 'date') {
									if (!isNaN(point2[prop]) && !isNaN(point1[prop]) && point2['date'] - point1['date'] !== 0) {
										let slope = (point2[prop] - point1[prop]) / (point2['date'] - point1['date']);
										datum[prop] = Math.round(slope * (point2['date'] - datum['date']) + point1[prop]);
									}
								}
							}
						}

						datums.push(datum);
					}

					currentTime += segmentDiff;
				}

				chartState.chart.addData(datums.sort(this._sortJobData));
			}

			this.changeDetectorRef.markForCheck();
		}
	}

	drop(event: CdkDragDrop<string[]>) {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		//this.resetTimeoutHandle();
		moveItemInArray(this.jobService.findUnitPeriodChannelsForJob(this.job.key), event.previousIndex, event.currentIndex);

		let userChannelViewModels: UserUnitPeriodChannelDto[] = [];
		let i = 0;
		this.jobService.findUnitPeriodChannelsForJob(this.job.key).forEach((jcv) => {
			jcv.channel.seq = i;
			let channelToUpdate: UserUnitPeriodChannelDto = this.findUserUnitPeriodChannels().find((uc) => uc.unitPeriodChannel.unitPeriodId === jcv.unitPeriodId && uc.unitPeriodChannel.channelId === jcv.channel.id);
			if (!channelToUpdate) {
				channelToUpdate = new UserUnitPeriodChannelDto();
			}
			channelToUpdate.seq = jcv.channel.seq;
			userChannelViewModels.push(channelToUpdate);
			i++;
		});

		this.jobService
			.putUserJobChannelSequences(this.jobKey, userChannelViewModels)
			.pipe(takeUntil(this.destroy))
			.subscribe(() => {});

		this._mergeChannels();
	}

	/**
	 * Creates a UserJobChannelDTO and calls the API to save Channel changes for User
	 * @param channelValue Job Channel that is being altered
	 */
	modifyChannel(channelValue: UserUnitPeriodChannelValue) {
		//this.resetTimeoutHandle();
		let channelToUpdate: UserUnitPeriodChannelDto = this.findUserUnitPeriodChannels().find((uc) => uc.unitPeriodChannel.id === channelValue.userUnitPeriodChannel.unitPeriodChannel.id);
		channelToUpdate.display = channelValue.userUnitPeriodChannel.display;
		channelToUpdate.showGraph = channelValue.userUnitPeriodChannel.showGraph;
		channelToUpdate.colorHex = channelValue.userUnitPeriodChannel.colorHex;
		channelToUpdate.lineStyle = channelValue.userUnitPeriodChannel.lineStyle;
		channelToUpdate.lineWeight = channelValue.userUnitPeriodChannel.lineWeight;

		this._updateUserJobChannel(channelToUpdate);
	}

	/**
	 * Updates the User Job Channel through the Job Service
	 * @param jobChannelToUpdate - User Job Channel that is being altered
	 */
	private _updateUserJobChannel(jobChannelToUpdate: UserUnitPeriodChannelDto) {
		this.jobService.updateUserJobChannel(this.jobKey, jobChannelToUpdate);
	}

	/*Chart Manipulation*/
	/**
	 * Toggles the visibility of a channel on the graph.
	 * @param channel Job Channel that will be toggled on Chart
	 */
	private _toggleChannelGraph(chartState: ChartState, channelValue: UserUnitPeriodChannelValue) {
		if (Object.prototype.hasOwnProperty.call(chartState.series, `${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`)) {
			if (!channelValue.userUnitPeriodChannel.unitPeriodChannel.channel.showGraph) {
				if (this.vertical) {
					chartState.series[`${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`].xAxis.disabled = true;
					chartState.series[`${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`].hide();
				} else {
					chartState.series[`${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`].yAxis.disabled = true;
					chartState.series[`${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`].hide();
				}
			} else {
				if (this.vertical) {
					chartState.series[`${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`].xAxis.disabled = false;
					chartState.series[`${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`].show();
				} else {
					chartState.series[`${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`].yAxis.disabled = false;
					chartState.series[`${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`].show();
				}
			}
		}
	}

	/**
	 * Toggles the visibility of a channel on the Channel Display
	 * @param channel Job Channel that will be toggled on Display
	 */
	toggleChannelVisibility(channelValue: UserUnitPeriodChannelValue) {
		if (this.isPublic) return;

		channelValue.userUnitPeriodChannel.display = !channelValue.userUnitPeriodChannel.display;

		this.modifyChannel(channelValue);
	}

	/**
	 * Highlights, or un-Highlights a series line. This is used on the UI when a Channel Display is hovered, causing the Channel Chart series to highlight (become bolder)
	 * @param userJobChannelValue User Job Channel that is being hovered in the display
	 * @param highlight Whether to highlight, or un-highlight the series
	 */
	highlightSeries(chartState: ChartState, userJobChannelValue: UserUnitPeriodChannelValue, highlight: boolean) {
		let channel = userJobChannelValue.userUnitPeriodChannel;
		if (Object.prototype.hasOwnProperty.call(chartState.series, `${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`)) {
			chartState.series[`${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`].strokeWidth = highlight ? channel.lineWeight * 2 : channel.lineWeight;

			if (this.vertical) {
				chartState.series[`${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`].xAxis.renderer.line.strokeWidth = highlight ? 3 : 1;
				chartState.series[`${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`].xAxis.renderer.fontWeight = highlight ? 'bold' : 'normal';
			} else {
				chartState.series[`${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`].yAxis.renderer.line.strokeWidth = highlight ? 3 : 1;
				chartState.series[`${channel.unitPeriodChannel.unitPeriodId}_${channel.unitPeriodChannel.channelId}`].yAxis.renderer.fontWeight = highlight ? 'bold' : 'normal';
			}
		}
	}

	/**
	 * Toggles the visibility of the Channel on the Chart
	 * @param channel The Job Channel to be toggled
	 */
	toggleChannelGraphVisibility(chartState: ChartState, channelValue: UserUnitPeriodChannelValue) {
		if (this.isPublic) return;

		channelValue.userUnitPeriodChannel.showGraph = !channelValue.userUnitPeriodChannel.showGraph;

		this.modifyChannel(channelValue);

		this._toggleChannelGraph(chartState, channelValue);
	}

	/**
	 * Modifies the color of the channel. This will be reflected on the Chart and Display
	 * @param channelId Id of Channel to change color for
	 * @param color Color to change the Channel to
	 */
	changeChannelColor(chartState: ChartState, unitPeriodId: number, channelId: number, color: string) {
		if (color && Object.prototype.hasOwnProperty.call(chartState?.series, `${unitPeriodId}_${channelId}`)) {
			let axis = chartState.chart.map.getKey(`axis-${unitPeriodId}_${channelId}`);

			if (axis) {
				// axis.renderer.line.strokeOpacity = 1;
				axis.renderer.line.strokeWidth = 1;
				axis.renderer.line.stroke = am4core.color(color);
				axis.renderer.labels.template.fill = am4core.color(color);

				chartState.series[`${unitPeriodId}_${channelId}`].stroke = am4core.color(color);
				chartState.series[`${unitPeriodId}_${channelId}`].tooltip.background.fill = am4core.color(color);
			}
		}
	}

	/**
	 * Changes the thickness of the line on the chart for a specific channel
	 * @param channelId Id of channel to change line weight for
	 * @param weight Weight (or thickness) of Channel series line
	 */
	changeLineWeight(chartState: ChartState, unitPeriodId: number, channelId: number, weight: number) {
		if (Object.prototype.hasOwnProperty.call(chartState?.series, `${unitPeriodId}_${channelId}`)) {
			chartState.series[`${unitPeriodId}_${channelId}`].strokeWidth = weight === 0 ? 1 : weight;
		}
	}

	/**
	 * Changes the style of the line on the chart for a specific channel
	 * @param channelId Id of channel to change line-style for
	 * @param lineStyle Style of line for channel sequence on chart
	 */
	changeLineStyle(chartState: ChartState, unitPeriodId: number, channelId: number, lineStyle: LineStyle) {
		if (Object.prototype.hasOwnProperty.call(chartState?.series, `${unitPeriodId}_${channelId}`)) {
			chartState.series[`${unitPeriodId}_${channelId}`].strokeDasharray = this._getStrokeDash(lineStyle);
		}
	}

	/**
	 * Changes the Min and Max for the Value-Axis of the chart for a specific channel. Each Channel has its own axis.
	 * @param channelId Id of channel to change axis-range for
	 * @param min Min value of the value-axis
	 * @param max Max value of the value-axis
	 */
	private _changeAxisRange(chartState: ChartState, unitPeriodId: number, channelId: number, min: number, max: number) {
		if (Object.prototype.hasOwnProperty.call(chartState?.series, `${unitPeriodId}_${channelId}`)) {
			let axis = chartState.chart.map.getKey(`axis-${unitPeriodId}_${channelId}`);
			if (axis) {
				if (!isNaN(min)) {
					axis.min = min;
				}

				if (!isNaN(max)) {
					axis.max = max;
				}
			}
		}
	}

	/**
	 * Determines sequence algorithm of dashes based off of LineStyle.
	 * @param lineStyle Style of line for channel sequence on chart
	 */
	private _getStrokeDash(lineStyle: LineStyle): string {
		switch (lineStyle) {
			case LineStyle.LongDash:
				return '8 4';
			case LineStyle.Dash:
				return '6 4';
			case LineStyle.DashedDot:
				return '8 2 1 2';
			case LineStyle.Dots:
				return '1 2';
			case LineStyle.SpacedDash:
				return '8 8';
			case LineStyle.Straight:
			default:
				return '';
		}
	}

	/**
	 * Changes the TimeSpan of the Chart and calls the API to get a new sample of points. This is used after a certain number of points have been loaded from SignalR and a refresh is needed.
	 */
	updateChartTimeSpanAndGetNewSamplePoints(chartState: ChartState) {
		this.zoomed = false;
		this.shifted = false;

		if (this.chartTimeSpanInMinutes == null) {
			this.chartTimeSpanInMinutes = 240;
		}

		let payload: GetJobDataPayload = {
			key: this.route.snapshot.paramMap.get('jobId'),
			unitPeriodIds: this.job.unitPeriods.map((unitPeriod) => unitPeriod.id),
			startDate: null,
			endDate: null,
			maxDate: this.job.end,
			zoomed: false,
			shifted: false,
			shift: null,
			minutes: this.chartTimeSpanInMinutes,
			operation: GetJobDataOperation.ChangeInterval,
		};

		this._setZoomAndShiftedAndTakeSampleAndUpdateChart(chartState, payload);
	}

	openOffsetChannelDialog(channelValue: UserUnitPeriodChannelValue) {
		this.dialog.open(OffsetChannelDialogComponent, {
			data: {
				userJobChannelValue: channelValue,
				key: this.jobKey,
			},
			width: '100%',
			maxWidth: '650px',
			height: '435px',
			autoFocus: false,
		});
	}

	openCalibrationDialog(channelValue: UserUnitPeriodChannelValue) {
		const dialogRef = this.dialog.open(CalibrationDialogComponent, {
			data: {
				userJobChannelValue: channelValue,
				key: this.jobKey,
			},
			width: '100%',
			maxWidth: '650px',
			height: '720px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((dto: CalibrationCloseDto) => {
				if (!dto) return;
				this.dcgService
					.forceRestartUnit(dto.unitGuid)
					.pipe(takeUntil(this.destroy))
					.subscribe({
						next: async () => {
							const message = 'Unit successfully re-started';
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						},
						error: () => {
							const message = 'Unit failed to re-start, please contact support for assistance';
							this.coreService.toast({ message: message, type: StatusType.DANGER });
						},
					});
			});
	}

	/**
	 * Opens the Color Picker dialog to allow a user to change a channel's color
	 * @param channel Channel that needs its color to be changed
	 */
	openColorPicker(chartState: ChartState, channelValue: UserUnitPeriodChannelValue) {
		if (this.isPublic) return;

		let validColors = this._presetColors.filter((pc) => {
			return !this.findUserUnitPeriodChannels().find(
				(c) =>
					c.colorHex === pc &&
					c.unitPeriodChannel.unitPeriodId !== channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId &&
					c.unitPeriodChannel.channel.id !== channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId
			);
		}, this);

		const dialogRef = this.dialog.open(ColorPickerComponent, {
			data: <ColorPickerData>{
				colors: validColors,
				label: channelValue.userUnitPeriodChannel.unitPeriodChannel.channel.label,
				type: ColorPickerType.Circle,
			},
			width: '100%',
			maxWidth: '300px',
			autoFocus: false,
		});

		dialogRef.componentInstance.colorChosen.pipe(this.takeUntilChartDisposed(chartState)).subscribe((result: ColorPickerChoice) => {
			if (Object.prototype.hasOwnProperty.call(chartState.series, `${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`)) {
				channelValue.userUnitPeriodChannel.colorHex = result.color;
				chartState.series[`${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`].stroke = am4core.color(result.color);
				chartState.series[`${channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId}_${channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId}`].tooltip.background.fill = am4core.color(result.color);
				//this.changeDetectorRef.markForCheck();
				this.modifyChannel(channelValue);
			}
		});
	}

	/**
	 * Opens the Comment Dialog to Add/Edit a comment
	 * @param jobCommentDto Job Comment to modify
	 * @param ev Event from Chart that is used when a bullet-point is hit. This allows the Dialog to find an existing comment for editing.
	 */
	openCommentAddDialog(chartState: ChartState, jobCommentDto: JobCommentDto, source: string, id: number) {
		if (id) {
			jobCommentDto = this.job.jobCommentReturn.jobComments.find((jc) => jc.id === id);
		}

		const dialogRef = this.dialog.open(CommentAddDialogComponent, {
			data: {
				jobComment: jobCommentDto,
				job: this.job,
				source: source,
				hideSaveActions: !this.isAdmin || this.isPublic,
			},
			width: '100%',
			maxWidth: this.isViewer ? '500px' : '450px',
			panelClass: 'comment-add-dialog-wrap',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(this.takeUntilChartDisposed(chartState))
			.subscribe((result1: { action: string; jobComment: JobCommentDto }) => {
				if (result1) {
					if (result1.action == 'insert') {
						this.jobService.addJobComment(this.jobKey, result1.jobComment);
						let attachmentIds = result1.jobComment.jobAttachments.map((ja) => {
							return ja.id;
						});

						this.jobService
							.addJobAttachmentsToComment(this.job.id, result1.jobComment.id, attachmentIds)
							.pipe(takeUntil(this.destroy))
							.subscribe((result2) => {});
					} else if (result1.action == 'update') {
						this.jobService.updateJobComment(this.jobKey, result1.jobComment);
						let attachmentIds = result1.jobComment.jobAttachments.map((ja) => {
							return ja.id;
						});

						this.jobService
							.addJobAttachmentsToComment(this.job.id, result1.jobComment.id, attachmentIds)
							.pipe(takeUntil(this.destroy))
							.subscribe((result2) => {});
					} else if (result1.action == 'delete') {
						this.jobService.deleteJobComment(this.jobKey, result1.jobComment.id);
					} else if (result1.action == 'selectDates') {
						this._selectDateAction = result1.action;
						var popup = chartState.chart.openPopup('<div>Click on plot area to select date/time</div>');
						popup.top = 10;
						popup.right = 30;
						popup.defaultStyles = false;
						popup.closable = false;
					} else if (result1.action == 'selectDate') {
						this._selectDateAction = result1.action;
						var popup = chartState.chart.openPopup('<div>Click on plot area to select start date/time</div>');
						popup.top = 10;
						popup.right = 30;
						popup.defaultStyles = false;
						popup.closable = false;

						this._selectedJobComment = result1.jobComment;
					}

					this.jobService.addComment = false;
				}
			});
	}

	/**Get the current Date Axis of Chart */
	private _getDateAxis(chartState: ChartState): am4charts.DateAxis {
		if (chartState?.chart) return chartState.chart.map.getKey('chart-date-axis') as am4charts.DateAxis;

		return null;
	}

	/**Change the interval that shows on the chart */
	changeChartInterval(chartState: ChartState, interval: number) {
		this.chartTimeSpanInMinutes = interval;
		const dateAxis = this._getDateAxis(chartState);

		if (dateAxis) {
			let payload: GetJobDataPayload = {
				key: this.route.snapshot.paramMap.get('jobId'),
				unitPeriodIds: this.job.unitPeriods.map((unitPeriod) => unitPeriod.id),
				startDate: new Date(dateAxis.minZoomed),
				endDate: new Date(dateAxis.maxZoomed),
				maxDate: this.job.end,
				zoomed: this.zoomed,
				shifted: this.shifted,
				shift: null,
				minutes: this.chartTimeSpanInMinutes,
				operation: GetJobDataOperation.ChangeInterval,
			};

			this._setZoomAndShiftedAndTakeSampleAndUpdateChart(chartState, payload);
		}
	}

	/**
	 * Assigns zoomed and shifted variables from payload and then takes a sample and updates the chart
	 * @param payload Job Payload to determine what date ranges the API needs to Sample data for
	 */
	private _setZoomAndShiftedAndTakeSampleAndUpdateChart(chartState: ChartState, payload: GetJobDataPayload) {
		this.zoomed = payload.zoomed;
		this.shifted = payload.shifted;

		this._takeSampleAndUpdateChart(chartState, payload);
	}

	/**
	 * Updates the UI values that are displayed for 'Elapsed Time:' and 'Viewing:'
	 * @param startDate Starting Date of Sample data being viewed
	 * @param endDate Ending Date of Sample data being viewed
	 */
	private _setChartTimeRange(chartState: ChartState, startDate: number, endDate: number) {
		const dateAxis = this._getDateAxis(chartState);

		if (!dateAxis) {
			console.log('No date axis');
			return;
		}
		this._chartTimeRange = {
			start: startDate || dateAxis.minZoomed,
			end: endDate || dateAxis.maxZoomed,
		};

		this.chartTimeRangeDisplayText = `${DateHelper.format(this.displayTimeZone(new Date(this._chartTimeRange.start)), 'm/d/yy HH:mm:ss')} - ${DateHelper.format(this.displayTimeZone(new Date(this._chartTimeRange.end)), DateHelper.sameDay(this.displayTimeZone(new Date(this._chartTimeRange.start)), this.displayTimeZone(new Date(this._chartTimeRange.end))) ? 'HH:mm:ss' : 'M/D/YYYY HH:mm:ss')}`;

		this.jobElapsedTimeDisplayText = this.getJobElapsedTimeFormatted();
	}

	/**
	 * If there is no data for a Channel on the chart, this method will hide the axis for that Channel.
	 */
	private _hideUnusedAxises(chartState: ChartState) {
		let userUnitPeriodChannels = this.findUserUnitPeriodChannels();

		userUnitPeriodChannels.sort((a, b) => {
			return a.seq - b.seq;
		});

		let i = 1;

		userUnitPeriodChannels.forEach((jcv) => {
			if (Object.prototype.hasOwnProperty.call(chartState.series, `${jcv.unitPeriodChannel.unitPeriodId}_${jcv.unitPeriodChannel.channelId}`)) {
				if (jcv.showGraph && i <= 10) {
					if (this.vertical) {
						chartState.series[`${jcv.unitPeriodChannel.unitPeriodId}_${jcv.unitPeriodChannel.channelId}`].xAxis.disabled = false;
						chartState.series[`${jcv.unitPeriodChannel.unitPeriodId}_${jcv.unitPeriodChannel.channelId}`].show();
					} else {
						chartState.series[`${jcv.unitPeriodChannel.unitPeriodId}_${jcv.unitPeriodChannel.channelId}`].yAxis.disabled = false;
						chartState.series[`${jcv.unitPeriodChannel.unitPeriodId}_${jcv.unitPeriodChannel.channelId}`].show();
					}
					i++;
				} else {
					if (this.vertical) {
						chartState.series[`${jcv.unitPeriodChannel.unitPeriodId}_${jcv.unitPeriodChannel.channelId}`].xAxis.disabled = true;
						chartState.series[`${jcv.unitPeriodChannel.unitPeriodId}_${jcv.unitPeriodChannel.channelId}`].show();
					} else {
						chartState.series[`${jcv.unitPeriodChannel.unitPeriodId}_${jcv.unitPeriodChannel.channelId}`].yAxis.disabled = true;
						chartState.series[`${jcv.unitPeriodChannel.unitPeriodId}_${jcv.unitPeriodChannel.channelId}`].show();
					}
				}
			}
		});
	}

	/**
	 * Adjusts the DateAxis min and max values and adjusts for gapping based off of date-range
	 * @param startDate Starting Date of Sample data being viewed
	 * @param endDate Ending Date of Sample data being viewed
	 */
	private _adjustDateAxisBoundsExtended(chartState: ChartState, startDate: Date, endDate: Date) {
		const dateAxis = this._getDateAxis(chartState);

		if (!dateAxis) return;

		this._adjustGaps(chartState);

		dateAxis.min = startDate.getTime();
		dateAxis.max = endDate.getTime();
	}

	/**Adjusts gapping of the chart based on the timespan of the Data retrieved */
	private _adjustGaps(chartState: ChartState) {
		if (this._jobResponse) {
			let secondDiff = (this._jobResponse.endDate - this._jobResponse.startDate) / 1000;

			if (secondDiff <= 1800) {
				//30 minutes or less, use 1 minute for gapping
				this._adjustSeriesGaps(chartState, 6 * this.job.unitPeriods.length);
			} else if (secondDiff <= 43200) {
				//30 minutes to 12 hrs, use 5 minutes for gapping
				this._adjustSeriesGaps(chartState, 60 * this.job.unitPeriods.length);
			} else if (secondDiff <= 86400) {
				//12 hrs to 24 hrs, use 15 minutes for gapping
				this._adjustSeriesGaps(chartState, 180 * this.job.unitPeriods.length);
			} else {
				//Anything for than 24hrs, use 30 minutes for gapping
				this._adjustSeriesGaps(chartState, (this.job.unitPeriods.length * secondDiff) / 240);
			}
		}
	}

	/**
	 * Loops through each series and applies the autoGapCount
	 * @param autoGapCount Number of missed points required to show a Gap in the series
	 */
	private _adjustSeriesGaps(chartState: ChartState, autoGapCount: number) {
		for (var prop in chartState.series) {
			chartState.series[prop].autoGapCount = autoGapCount;
		}
	}

	/**
	 * Flatten's data into a JobDatumFlat model to pass to the chart as Series data
	 * @param data Job data from API response
	 */
	private _flattenData(data: JobData): JobDatumFlat {
		let datum = { date: new Date(data.d).getTime() } as JobDatumFlat;

		data.c.forEach((c) => {
			datum[`${data.u}_${c.i}`] = c.v;
			//datum[c.i] = c.v;
		});
		return datum;
	}

	/**
	 * Adjusts for a proper payload for rendering the chart based on the current Chart Interval and the shiftType
	 * @param shiftType The direction and size of shift
	 */
	shiftChart(chartState: ChartState, shiftType: ShiftType) {
		this.shifted = true;
		const dateAxis = this._getDateAxis(chartState);

		let payload: GetJobDataPayload;

		let intervalCalc = this.chartTimeSpanInMinutes;
		if (this.chartTimeSpanInMinutes == null) {
			let diff = (dateAxis.maxZoomed - dateAxis.minZoomed) / 60000;
			intervalCalc = diff;
		}

		payload = {
			key: this.route.snapshot.paramMap.get('jobId'),
			unitPeriodIds: this.job.unitPeriods.map((unitPeriod) => unitPeriod.id),
			startDate: new Date(dateAxis.minZoomed),
			endDate: new Date(dateAxis.maxZoomed),

			maxDate: this.job.end,
			zoomed: this.zoomed,
			shifted: true,
			shift: shiftType,
			minutes: intervalCalc,
			operation: GetJobDataOperation.Shift,
		};

		this._setZoomAndShiftedAndTakeSampleAndUpdateChart(chartState, payload);
	}

	/**
	 * Toggles the ability to see Tooltips on each line series
	 */
	toggleFlags(chartState: ChartState, disabled = null) {
		if (disabled == null) this.showTooltips = !this.showTooltips;

		for (var prop in chartState.series) {
			if (prop !== '0_0' && Object.prototype.hasOwnProperty.call(chartState.series, prop)) {
				if (!disabled) {
					chartState.series[prop].tooltip.disabled = !this.showTooltips;
				} else {
					chartState.series[prop].tooltip.disabled = disabled;
				}
			}
		}
	}

	/**
	 * For mobile view, looks at the value of the channel and applies a blinking red, or blinking yellow class to the channel if a threshold is exceeded
	 * @param channelValue The Channel Information and its current value
	 */
	getThresholdClass(channelValue: UserUnitPeriodChannelValue): string {
		let jobChannel = this.jobService
			.findUnitPeriodChannelsForJob(this.job.key)
			.find((jc) => jc.unitPeriodId == channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId && jc.channelId == channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId);

		if (jobChannel) {
			if ((jobChannel.lowDanger != null && channelValue.value <= jobChannel.lowDanger) || (jobChannel.highDanger != null && channelValue.value >= jobChannel.highDanger)) {
				return 'blink-red';
			} else if ((jobChannel.lowWarning != null && channelValue.value <= jobChannel.lowWarning) || (jobChannel.highWarning != null && channelValue.value >= jobChannel.highWarning)) {
				return 'blink-yellow';
			}
		}
		return '';
	}

	/* Widget Handling*/

	/**
	 * Emits an Output event to close the Job Chart widget in the Dashboard view
	 */

	rotateWidget() {
		this.vertical = !this.vertical;
		this.onRotate.emit(this.vertical);
	}

	deleteWidget() {
		this.onDelete.emit(true);
	}

	copyWidget() {
		this.onCopy.emit();
	}

	/*Utils */
	/**
	 * Checks to see if a number exists. 0 would still exist here, but null would not
	 * @param val Value to check to see if number exists
	 */
	private _numberExists(val: number): boolean {
		return !(val == null);
	}

	/**
	 * Gets the difference in time as a nice, formatted string based off of the Start and End Date of axis
	 */
	private getJobElapsedTimeFormatted(): string {
		if (!this.job && !this.lastUpdatedDate) return '--';

		let lastDate = this.lastUpdatedDate || this.job?.end;
		let startDate = this.job.start ? this.job.start.getTime() : this.job.unitPeriods[0]?.dateStarted.getTime();

		if (!lastDate) return '--';

		let totalSeconds = Math.floor((new Date(lastDate).getTime() - startDate) / 1000); // seconds total
		let totalDays = Math.floor(totalSeconds / (60 * 60 * 24));
		totalSeconds %= 60 * 60 * 24;
		let totalHours = Math.floor(totalSeconds / (60 * 60));
		totalSeconds %= 60 * 60;
		let totalMinutes = Math.floor(totalSeconds / 60);
		totalSeconds %= 60;

		let totalTime: string = '';
		if (totalDays > 0) totalTime += `${totalDays}d `;
		if (totalHours > 0) totalTime += `${totalHours}h `;
		if (totalMinutes > 0) totalTime += `${totalMinutes}m `;
		if (totalSeconds > 0) totalTime += `${totalSeconds}s `;

		return totalTime;
	}

	/**
	 *
	 */
	getTimeDiffMins(): number {
		if (!this.job || !this.lastUpdatedDate) return 0;

		let totalSeconds = Math.floor((this.lastUpdatedDate.getTime() - (this.job.start ? this.job.start.getTime() : this.job.unitPeriods[0]?.dateStarted.getTime())) / 1000);
		let totalHours = Math.floor(totalSeconds / 60);

		return totalHours;
	}

	/**
	 * Determines the class to apply to the Channel Display values, depending on the number of User Job Channels are being shown
	 */
	private _getChannelClass() {
		var count = 0;

		if (!this.isEditing) {
			count = this.userJobChannelValuesToShow.length || this.findUserUnitPeriodChannels().filter((c) => c.display).length;
		} else {
			count = this.findUserUnitPeriodChannels().length;
		}

		if (count > 15) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-6 row-cols-xl-6';
		}
		if (count == 15) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-5 row-cols-lg-5 row-cols-xl-5';
		}
		if (count == 14) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-5 row-cols-lg-5 row-cols-xl-5';
		}
		if (count == 13) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-4 row-cols-xl-4';
		}
		if (count == 12) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-6 row-cols-xl-6';
		}
		if (count == 11) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-6 row-cols-xl-6';
		}
		if (count == 10) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-5 row-cols-lg-5 row-cols-xl-5';
		}
		if (count == 9) {
			this.channelClass = 'row-cols-2 row-cols-sm-3 row-cols-md-5 row-cols-lg-5 row-cols-xl-5';
		}
		if (count == 8) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-4 row-cols-xl-4';
		}
		if (count == 7) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-4 row-cols-xl-4';
		}
		if (count == 6) {
			this.channelClass = 'row-cols-2 row-cols-sm-3 row-cols-md-3 row-cols-lg-6 row-cols-xl-6';
		}
		if (count == 5) {
			this.channelClass = 'row-cols-2 row-cols-sm-3 row-cols-md-3 row-cols-lg-5 row-cols-xl-5';
		}
		if (count == 4) {
			this.channelClass = 'row-cols-2 row-cols-sm-4 row-cols-md-4 row-cols-lg-4 row-cols-xl-4';
		}
		if (count == 3) {
			this.channelClass = 'row-cols-2 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-4';
		}
		if (count == 2) {
			this.channelClass = 'row-cols-2 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-4';
		}
		if (count == 0) {
			this.channelClass = '';
		}
	}

	/**
	 * Event for when the FAB is clicked or swiped in mobile view. This will ultimately determine if the Save button should be shown or not.
	 */
	onFabClickOrSwipe() {
		if (!this.isPublic) {
			this.displayFab = true;
			this.editingFromFab = !this.editingFromFab;
			if (this.editingFromFab) {
				this.disableDragging = false;
				if (this.fabTimeout) clearTimeout(this.fabTimeout);
			} else {
				this.disableDragging = true;
				this.displayFab = false;
			}
		}
	}

	/**
	 * After data has been initialized, or if the Chart has changed from Horizontal to Vertical (or vice-versa), this method dynamically binds the data to the chart and determine's chart properties, as well as create's all of the axis's necessary.
	 */
	private _bindData() {
		const chartData: Array<JobDatumFlat> = [];
		if (!this.hasInitialized || this._forceRefresh) {
			let chartState: ChartState;
			let chart: XYChart;

			this.zone.runOutsideAngular(() => {
				this._forceRefresh = false;

				am4core.options.minPolylineStep = 5;
				am4core.options.autoDispose = false;

				this.chartState$.value?.dispose();
				chartState = new ChartState(am4core.create(this.chartDiv.nativeElement, am4charts.XYChart), this.zone);
				chart = chartState.chart;

				chart.dateFormatter.timezoneOffset = this.job.utcDateTimeOffset * -60 || this.timezoneService.getCurrentTimeZone() * -60;

				chart.tapToActivate = true;
				chart.tapTimeout = 5000;
				chart.dragGrip.disabled = false;

				chart.colors.step = 2;
				chart.paddingTop = 10;
				chart.paddingRight = 0;
				chart.paddingBottom = 0;
				chart.paddingLeft = 0;

				chart.zoomOutButton.disabled = true;

				// Use subjects to collect amchart events, and then subscribe against those subjects.
				// This avoids race conditions caused by using fromEvent(chart.events,...) which can
				// sometimes run against already disposed amchart EventDispatchers.
				const beforedatavalidated$ = new Subject<{
					type: 'beforedatavalidated';
					target: XYChart;
				}>();
				chart.events.on('beforedatavalidated', (ev) => {
					beforedatavalidated$.next(ev);
				});

				const hit$ = new Subject<
					{
						type: 'hit';
						target: XYChart;
					} & SpritePointerTypeEvent &
						SpritePointEvent &
						SpriteMouseTouchEvent
				>();
				chart.events.on('hit', (ev) => {
					hit$.next(ev);
				});

				const validated$ = new Subject<{
					type: 'validated';
					target: XYChart;
				}>();
				chart.events.on('validated', (ev) => {
					validated$.next(ev);
				});

				beforedatavalidated$.pipe(this.takeUntilChartDisposed(chartState)).subscribe(() => {
					if (chart.data && chart.data.length > 0) {
						chart.data.sort(this._sortJobData);
					}
				});

				hit$.pipe(this.takeUntilChartDisposed(chartState)).subscribe(
					(
						ev: {
							type: 'hit';
							target: XYChart;
						} & SpritePointerTypeEvent &
							SpritePointEvent &
							SpriteMouseTouchEvent
					) => {
						if (this._selectDateAction === 'selectDate') {
							const xAxis: any = this.vertical ? chartState.chart.yAxes.getIndex(0) : chart.xAxes.getIndex(0);
							const point = am4core.utils.documentPointToSprite(ev.point, chartState.chart.seriesContainer);
							const date = xAxis.xToValue(point.x);

							this._selectedJobComment.commentDate = new Date(date);

							this.openCommentAddDialog(chartState, this._selectedJobComment, 'job-chart', null);

							chart.closeAllPopups();
							this._selectDateAction = null;
						}
					}
				);

				// Create axes
				const dateAxis = this.vertical ? chart.yAxes.push(new am4charts.DateAxis()) : chart.xAxes.push(new am4charts.DateAxis());

				if (this.vertical) {
					dateAxis.renderer.inversed = true;
					dateAxis.renderer.labels.template.rotation = 270;
					dateAxis.renderer.labels.template.padding(0, 10, 0, 10);
					dateAxis.tooltip.rotation = 270;
				}

				dateAxis.id = 'chart-date-axis';

				dateAxis.renderer.minGridDistance = 100;

				dateAxis.tooltipDateFormat = 'MM-dd-yyyy HH:mm:ss';

				// Y-Axis Grid Lines
				dateAxis.renderer.grid.template.strokeOpacity = 0.7;
				dateAxis.renderer.grid.template.stroke = am4core.color('grey');
				dateAxis.renderer.grid.template.strokeDasharray = '1 8';

				dateAxis.baseInterval = {
					count: 1,
					timeUnit: 'second',
				};

				chart.cursor = new am4charts.XYCursor();

				validated$.pipe(debounceTime(500), this.takeUntilChartDisposed(chartState)).subscribe(() => {
					const dateAxisInner = this._getDateAxis(chartState);

					if (!this.isMobile) {
						this._setChartTimeRange(chartState, dateAxisInner.min, dateAxisInner.max);
					}

					if (!this._initializedChart) {
						this._initializedChart = true;

						const rangechangeended$ = new Subject<IDateAxisEvent>();
						dateAxisInner.events.on('rangechangeended', (ev) => {
							rangechangeended$.next(ev);
						});

						rangechangeended$
							.pipe(
								map((event: IDateAxisEvent) => event.target),
								debounceTime(500),
								this.takeUntilChartDisposed(chartState)
							)
							.subscribe((target: am4charts.DateAxis<am4charts.AxisRenderer>) => {
								this._onZoom(chartState, target);
							});
					}
				});

				let valueAxis = this.vertical ? chart.xAxes.push(new am4charts.ValueAxis()) : chart.yAxes.push(new am4charts.ValueAxis());

				valueAxis.min = valueAxis.minZoomed;
				valueAxis.max = valueAxis.maxZoomed;
				valueAxis.renderer.opposite = true;
				valueAxis.renderer.line.strokeOpacity = 1;
				valueAxis.renderer.line.strokeWidth = 1;
				valueAxis.renderer.line.hidden = true; // Hides the line in the end of chart
				valueAxis.renderer.line.stroke = am4core.color('grey');
				valueAxis.renderer.labels.template.fill = am4core.color('grey');

				// X-Axis Grid Lines
				valueAxis.renderer.grid.template.strokeOpacity = 0.5;
				valueAxis.renderer.grid.template.strokeWidth = 1;
				valueAxis.renderer.grid.template.stroke = am4core.color('#DCDCDC');

				this._createAxisAndSeries(chartState, 0, 0, 'Comments', 'black', false, LineStyle.SpacedDash, 1, null);

				this.findUserUnitPeriodChannels().forEach((ujc) => {
					if (chart.series.length < 11) {
						let channelLabel = ujc.unitPeriodChannel.channel.label;

						if (ujc.unitPeriodChannel.channel.units) channelLabel += ` (${ujc.unitPeriodChannel.channel.units})`;

						// let hidden = (ujc.unitPeriodChannel.channel.options & ChannelOptions.HiddenGraph) !== 0;
						this._createAxisAndSeries(chartState, ujc.unitPeriodChannel.unitPeriodId, ujc.unitPeriodChannel.channel.id, channelLabel, ujc.colorHex, !ujc.showGraph, ujc.lineStyle, ujc.lineWeight, ujc);
					}
				});

				chartData.sort((a, b) => {
					return a.date - b.date;
				});

				// hide/show parts of chart based on rules
				chart.responsive.enabled = true;
				chart.responsive.useDefault = false;

				chart.cursor.behavior = this.cursorBehavior || (this.vertical ? 'zoomY' : 'zoomX');

				if (chart.cursor.behavior === 'selectX') {
					chart.cursor.events.on('selectended', (ev) => {
						let range = ev.target.xRange;
						//let xAxis:any = this.vertical ? ev.target.chart.yAxes.getIndex(0) : ev.target.chart.xAxes.getIndex(0);

						let xAxis: any = ev.target.chart.xAxes.getIndex(0);

						//Filter data to pass to table from client side
						let startDate = range && range.start ? xAxis.positionToDate(xAxis.toAxisPosition(range.start)) : null;
						let endDate = range && range.end ? xAxis.positionToDate(xAxis.toAxisPosition(range.end)) : null;
					});
				}

				this._updateChartDataAndCommentSeries(chartState, chartData);

				this._hideUnusedAxises(chartState);

				this._setChartTimeRange(chartState, chartData && chartData.length > 0 ? chartData[0].date : null, chartData && chartData.length > 0 ? chartData[chartData.length - 1].date : null);

				this._setDateMinMax(chartState, this._chartTimeRange.start, this._chartTimeRange.end);

				this.subscribeToEvents(chartState);
			});

			this.hasInitialized = true;
			this.chartState$.next(chartState);
		} else {
			this._updateChartDataAndCommentSeries(this.chartState$.value, chartData);

			this._hideUnusedAxises(this.chartState$.value);

			this._setChartTimeRange(this.chartState$.value, chartData && chartData.length > 0 ? chartData[0].date : null, chartData && chartData.length > 0 ? chartData[chartData.length - 1].date : null);

			this._setDateMinMax(this.chartState$.value, this._chartTimeRange.start, this._chartTimeRange.end);
		}
	}

	/**
	 * Subscribing to all Observables necessary for this Component.
	 */
	private subscribeToEvents(chartState: ChartState) {
		this.jobService.newDataSent$
			.pipe(share())
			.pipe(this.takeUntilChartDisposed(chartState))
			.subscribe((data) => {
				if (data && data.length > 0 && (data[0].k === this.jobKey || (this.jobId && data[0].k === String(this.jobId)))) {
					this._bufferedLiveData.push(...data);
				}
			});

		this.jobHub.refreshFromDisconnect$.pipe(this.takeUntilChartDisposed(chartState)).subscribe((result) => {
			if (result) {
				console.log('Disconnected from SignalR, refreshing...');
				this.changeChartInterval(chartState, this.chartTimeSpanInMinutes);
			}
		});

		this.dashboardService.gridsterItemUpdated$.pipe(this.takeUntilChartDisposed(chartState)).subscribe((gridsterItem) => {
			if (gridsterItem && this.gridsterItem && gridsterItem.chartName == this.gridsterItem.chartName) {
				this.gridsterItem = gridsterItem;

				this._chartSizeChangeAction();
			}
		});

		this.commentData$.pipe(takeUntil(this.destroy)).subscribe((commentData) => {
			if (commentData) {
				let allJobCommentDefaultGroups = UtilityHelper.deepCopy(commentData?.jobCommentDefaultGroups);
				this.jobCommentDefaultGroups = allJobCommentDefaultGroups.filter((jcdg) => jcdg.jobTypeId === this.job?.jobTypeId);
				this.filterJobCommentDefaults();
			}
		});
	}

	/**
	 * Determines if the Chart is Horizontal or Vertical and adjusts the layout appropriately
	 */
	private _chartSizeChangeAction() {
		const refreshChartData = () => {
			this._forceRefresh = true;
			this._bindData();
		};

		// TODO Isaac commenting this out per Brads instructions
		// if (!this.isMobile && this.el && this.el.nativeElement && this.el.nativeElement.clientHeight > this.el.nativeElement.clientWidth) {
		// 	if (!this.vertical) {
		// 		this.vertical = true;
		// 		refreshChartData();
		// 	}
		// } else {
		// 	if (this.vertical) {
		// 		this.vertical = false;
		// 		refreshChartData();
		// 	}
		// }

		if (this.vertical) {
			this.vertical = false;
			refreshChartData();
		}
	}

	/**
	 * Final step in drawing the Chart correctly. This method transforms the data as needed and passes it along to other methods to draw the chart appropriately
	 */
	private _drawChart(chartState: ChartState) {
		// If no Last Updated Date on initial load, go ahead and set it to last point value

		if (!this.lastUpdatedDate && this._jobReturnCaches.length > 0) {
			let latestDate: number = 0;

			this._jobReturnCaches.forEach((jrc) => {
				if (jrc?.data && jrc.data.length > 2 && jrc.data[jrc.data.length - 1].unix * 1000 > latestDate) latestDate = jrc.data[jrc.data.length - 1].unix * 1000;
			});
			this.lastUpdatedDate = new Date(latestDate);
		}

		let datum: JobDatumFlat[] = [];

		if (this._jobReturnCaches.length > 0) {
			let jobDataMerged: JobReturnData[] = [];

			this._jobReturnCaches.forEach((jrc) => {
				if (jrc) jobDataMerged = jobDataMerged.concat(jrc.data);
			});

			jobDataMerged.sort((a, b) => {
				return a.unix - b.unix;
			});

			if (jobDataMerged.length > 1) {
				let startDate = jobDataMerged[0].unix;
				let endDate = jobDataMerged[jobDataMerged.length - 1].unix;

				let diffTime = endDate - startDate;

				let interval = diffTime < 1000 ? 1 : (endDate - startDate) / 1000;

				for (let currentDate = startDate; currentDate <= endDate; currentDate += interval) {
					let jobDatumFlat: JobDatumFlat = {
						date: currentDate * 1000,
					};

					this._jobReturnCaches.forEach((jrc) => {
						let dataInTimeSpan = jrc.data.filter((d) => d.unix >= currentDate && d.unix < currentDate + interval);
						dataInTimeSpan.forEach((d) => {
							for (let i = 0; i < jrc.channelIds.length; i++) {
								let channelId = jrc.channelIds[i];

								if (!this.findUserUnitPeriodChannels().find((ujc) => ujc.showGraph && ujc.unitPeriodChannel.unitPeriodId === jrc.unitPeriodId && ujc.unitPeriodChannel.channelId === channelId)) {
									continue;
								}

								let channelValue = d.data[i];

								let unitPeriodChannel = this.jobService.findUnitPeriodChannelsForJob(this.job.key).find((dc) => dc.unitPeriodId === jrc.unitPeriodId && dc.channelId === channelId);

								// Filtering invalid data
								let valid =
									!!unitPeriodChannel.channel &&
									((this._numberExists(unitPeriodChannel.channel.min) && channelValue >= unitPeriodChannel.channel.min) || !this._numberExists(unitPeriodChannel.channel.min)) &&
									((this._numberExists(unitPeriodChannel.channel.max) && channelValue <= unitPeriodChannel.channel.max) || !this._numberExists(unitPeriodChannel.channel.max));

								if (valid) {
									jobDatumFlat[`${jrc.unitPeriodId}_${channelId}`] = d.data[i];
								}
							}

							datum.push(jobDatumFlat);
						});
					});
				}
			}

			// Applying Precision
			datum.forEach((d) => {
				for (var prop in d) {
					if (d[prop] !== null) {
						let precision: number;
						if (prop !== 'date') {
							let upid = prop.split('_')[0];
							let channelId = prop.split('_')[1];
							precision = this.jobService.findUnitPeriodChannelsForJob(this.job.key).find((jc) => String(jc.unitPeriodId) === upid && String(jc.channelId) === channelId)?.channel?.precision;
						} else {
							precision = this.jobService.findUnitPeriodChannelsForJob(this.job.key).find((jc) => String(jc.channelId) === prop)?.channel?.precision;
						}
						d[prop] = +d[prop].toFixed(precision);
					}
				}
			});

			datum.sort((a, b) => {
				return a.date - b.date;
			});
		}

		let startDate = new Date(this._jobResponse.startDate);
		let endDate = new Date(this._jobResponse.endDate);

		this._adjustDateAxisBoundsExtended(chartState, startDate, endDate);

		this._setChartTimeRange(chartState, startDate.getTime(), endDate.getTime());

		this._updateChartDataAndCommentSeries(chartState, datum);

		this.reloadingData = false;

		//Based off of response, the left and right arrows will be enabled/disabled accordingly
		this.endLeftShift = this._jobResponse.leftShiftEnded;
		this.endRightShift = this._jobResponse.rightShiftEnded;

		//Only hiding unused axises here after data has been initialize. Otherwise, the chart does not draw.
		if (this.initializingData) {
			this.initializingData = false;
		} else {
			this._hideUnusedAxises(chartState);
		}
	}

	/**
	 * Gets the average value of an array of numbers
	 * @param numbers Array of numbers to find the average value for
	 */
	private _averageValue(numbers: number[]) {
		let validNumbers = numbers.filter((n) => n !== null);

		if (validNumbers.length === 0) return null;

		return validNumbers.reduce((a, b) => a + b) / validNumbers.length;
	}

	/**
	 * Event that is fired when the Chart is zoomed in on
	 */
	private _onZoom(chartState: ChartState, target: am4charts.DateAxis<am4charts.AxisRenderer>) {
		if (this._blockDataApiCall) {
			this._blockDataApiCall = false;
			return;
		}

		let startDate: Date = new Date(target.minZoomed);
		let endDate: Date = new Date(target.maxZoomed);

		if (this._chartTimeRange && Math.abs(this._chartTimeRange.start - target.minZoomed) <= 5000 && (!this._chartTimeRange.end || Math.abs(this._chartTimeRange.end - target.maxZoomed) <= 5000)) {
			return;
		}

		//Double zooming was happening because of how AM Charts detects zoom. Function will not run if Zoom window has not changed by more than 1 minute
		if (this._lastMinZoomed !== undefined && Math.abs(this._lastMinZoomed - target.minZoomed) < 60000 && this._lastMaxZoomed !== undefined && Math.abs(this._lastMaxZoomed - target.maxZoomed) < 60000) {
			return;
		}

		this._lastMinZoomed = target.minZoomed;
		this._lastMaxZoomed = target.maxZoomed;

		this.changeDetectorRef.markForCheck();

		let payload: GetJobDataPayload = {
			key: this.route.snapshot.paramMap.get('jobId'),
			unitPeriodIds: this.job.unitPeriods.map((unitPeriod) => unitPeriod.id),
			startDate: startDate,
			endDate: endDate,
			maxDate: this.job.end,
			zoomed: true,
			shifted: this.shifted,
			minutes: null,
			operation: GetJobDataOperation.Zoom,
		};

		this._setZoomAndShiftedAndTakeSampleAndUpdateChart(chartState, payload);

		this.chartTimeSpanInMinutes = null;

		//Using default change detection now, but still need to markForCheck on zooming because of AM Charts issue
		this.changeDetectorRef.markForCheck();
	}

	/**
	 * Sets the minimum and maximum dates of the Date Axis
	 * @param min Min value of the Date Axis. In Unix milliseconds
	 * @param max Max value of the Date Axis. In Unix milliseconds
	 * @param reloadGaps If true, the gapping on the chart will reload
	 */
	private _setDateMinMax(chartState: ChartState, min: number, max: number, reloadGaps = true) {
		const dateAxis = this._getDateAxis(chartState);
		if (dateAxis) {
			dateAxis.min = min; // Adds gap in the beginning
			dateAxis.max = max; // Adds gap in the end
			if (reloadGaps) this._adjustGaps(chartState);
		}
	}

	/**
	 * Used to sort values in an array of JobDatum's
	 */
	private _sortJobData(a: JobDatumFlat, b: JobDatumFlat) {
		return a.date - b.date;
	}

	/**
	 * Creates an Axis for a Series
	 * @param chart AMChart to create and axis on
	 * @param field Unique identifier used to name the series. Typically the ChannelId, but 0 for Comments
	 * @param name Name of series
	 * @param color Color of series line
	 * @param hidden Determines whether the series is hidden or shown
	 * @param lineStyle Line style of the series line
	 * @param lineWeight Line weight of the series line
	 * @param userJobChannel User Job Channel that the series is applicable too
	 */
	private _createAxisAndSeries(chartState: ChartState, unitPeriodId: number, field: number, name: string, color: string, hidden: boolean, lineStyle: LineStyle, lineWeight: number, userJobChannel: UserUnitPeriodChannelDto) {
		if (field !== 0 && (hidden || !userJobChannel.showGraph)) return;
		const chart = chartState.chart;
		let series: am4charts.LineSeries;

		series = chart.series.push(new am4charts.LineSeries());

		if (this.vertical) {
			series.dataFields.valueX = `${unitPeriodId}_${field}`;
			series.dataFields.dateY = 'date';
		} else {
			series.dataFields.valueY = `${unitPeriodId}_${field}`;
			series.dataFields.dateX = 'date';
		}

		series.strokeWidth = lineWeight === 0 ? 1 : lineWeight;
		series.name = name;
		if (field !== 0) {
			if (this.vertical) {
				series.tooltipText = `${name}: [bold]{valueX}[/]`;
			} else {
				series.tooltipText = `${name}: [bold]{valueY}[/]`;
			}
		}

		series.tooltip.getFillFromObject = false;
		series.tooltip.background.fill = am4core.color(color);
		series.smoothing = 'monotoneX';
		series.id = `${unitPeriodId}_${field}`;
		series.showOnInit = false;
		series.stroke = am4core.color(color);
		series.strokeOpacity = 1;
		series.togglable = false;
		series.hidden = hidden;
		series.strokeDasharray = this._getStrokeDash(lineStyle);

		// Data Gap
		series.connect = false;
		series.autoGapCount = 20;

		if (field === 0) {
			series.connect = true;
			series.tooltip.label.interactionsEnabled = true;
			series.tooltip.keepTargetHover = true;
			series.tooltip.pointerOrientation = 'vertical';
			series.tooltip.label.interactions.events.on('over', () => {
				this.toggleFlags(chartState, true);
			});

			series.tooltip.label.interactions.events.on('out', () => {
				this.toggleFlags(chartState, false);
			});

			var bullet = series.bullets.push(new am4charts.Bullet());
			bullet.tooltipHTML = '{comment}';

			var image = bullet.createChild(am4core.Image);
			image.width = 20;
			image.height = 20;
			image.horizontalCenter = 'middle';
			image.verticalCenter = 'middle';
			image.propertyFields.href = 'bullet';

			const over$ = new Subject<any>();
			bullet.events.on('over', (ev) => {
				over$.next(ev);
			});
			over$.pipe(this.takeUntilChartDisposed(chartState)).subscribe((ev) => {
				this.toggleFlags(chartState, true);
			});

			const out$ = new Subject<any>();
			bullet.events.on('out', (ev) => {
				out$.next(ev);
			});
			out$.pipe(this.takeUntilChartDisposed(chartState)).subscribe((ev) => {
				this.toggleFlags(chartState, false);
			});

			let valueAxis = this.vertical ? chart.xAxes.values.find((v) => v.id === 'axis-comments') : chart.yAxes.values.find((v) => v.id === 'axis-comments');

			if (!valueAxis) {
				if (this.vertical) {
					valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
					series.xAxis = valueAxis;
					series.xAxis.disabled = hidden;
					series.xAxis.hide();
				} else {
					valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
					series.yAxis = valueAxis;
					series.yAxis.disabled = hidden;
					series.yAxis.hide();
				}

				valueAxis.id = `axis-${unitPeriodId}_${field}`;
				valueAxis.tooltip.disabled = true; // removes the black box hover yAxis value

				valueAxis.renderer.line.strokeOpacity = 1;
				valueAxis.renderer.line.strokeWidth = 1;
				valueAxis.renderer.line.stroke = series.stroke;
				valueAxis.renderer.labels.template.fill = series.stroke;

				//For smaller devices, rotate value axis and reduce padding
				if (this.vertical) {
					valueAxis.renderer.labels.template.rotation = 0;
					valueAxis.renderer.labels.template.padding(5, 10, 5, 10);
					valueAxis.renderer.labels.template.textAlign = 'middle';
					valueAxis.renderer.minGridDistance = 60;
				} else {
					valueAxis.renderer.labels.template.rotation = 270;
					valueAxis.renderer.labels.template.padding(5, 10, 5, 10);
					valueAxis.renderer.labels.template.textAlign = 'middle';
					valueAxis.renderer.minGridDistance = 60;
				}

				// X-Axis Grid Lines
				valueAxis.renderer.grid.template.disabled = true;
				valueAxis.renderer.grid.template.stroke = series.stroke;
				valueAxis.renderer.grid.template.strokeDasharray = '1 8';

				valueAxis['min'] = 0;
				valueAxis['max'] = 1;

				valueAxis.width = 0;
			}

			if (this.vertical) {
				series.xAxis = valueAxis;
			} else {
				series.yAxis = valueAxis;
			}
		} else {
			let valueAxis: am4charts.ValueAxis<am4charts.AxisRenderer>;
			if (this.vertical) {
				valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
				series.xAxis = valueAxis;
				series.xAxis.disabled = hidden;
			} else {
				valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
				series.yAxis = valueAxis;
				series.yAxis.disabled = hidden;
			}

			valueAxis.id = `axis-${unitPeriodId}_${field}`;
			valueAxis.tooltip.disabled = true; // removes the black box hover yAxis value

			valueAxis.renderer.line.strokeOpacity = 1;
			valueAxis.renderer.line.strokeWidth = 1;
			valueAxis.renderer.line.stroke = series.stroke;
			valueAxis.renderer.labels.template.fill = series.stroke;
			if (this.vertical) valueAxis.renderer.opposite = true;

			//For smaller devices, rotate value axis and reduce padding
			if (this.vertical) {
				valueAxis.renderer.labels.template.rotation = 0;
				valueAxis.renderer.labels.template.padding(0, 10, 0, 10);
				valueAxis.renderer.labels.template.textAlign = 'middle';
				valueAxis.renderer.minGridDistance = 60;
			} else {
				valueAxis.renderer.labels.template.rotation = 270;
				valueAxis.renderer.labels.template.padding(5, 10, 5, 10);
				valueAxis.renderer.labels.template.textAlign = 'middle';
				valueAxis.renderer.minGridDistance = 60;
			}

			// X-Axis Grid Lines
			valueAxis.renderer.grid.template.disabled = true;
			valueAxis.renderer.grid.template.stroke = series.stroke;
			valueAxis.renderer.grid.template.strokeDasharray = '1 8';

			if (userJobChannel.minY || userJobChannel.minY === 0) {
				valueAxis.min = userJobChannel.minY;
			}

			if (userJobChannel.maxY || userJobChannel.maxY === 0) {
				valueAxis.max = userJobChannel.maxY;
			}

			if (!userJobChannel.showGraph) {
				if (this.vertical) {
					series.xAxis.disabled = true;
				} else {
					series.yAxis.disabled = true;
				}

				series.hide();
			}
		}

		chartState.series[`${unitPeriodId}_${field}`] = series;
	}

	editUserChannelSettings(chartState: ChartState) {
		if (this.isPublic) {
			return;
		}
		const channels: UserUnitPeriodChannelDto[] = UtilityHelper.deepCopy(this.findUserUnitPeriodChannels());
		const dialogRef = this.dialog.open(EditUserChannelsDialogComponent, {
			data: {
				key: this.jobKey,
				userUnitPeriodChannels: channels,
				isWidget: this.isWidget,
			},
			width: this.isWidget ? '60%' : '100%',
			maxWidth: '1400px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(this.takeUntilChartDisposed(chartState))
			.subscribe((data: { key: string; userJobChannels: UserUnitPeriodChannelDto[] }) => {
				if (data) {
					if (!this.gridsterItem) {
						let i = 0;

						data.userJobChannels.forEach((ujc) => {
							ujc.seq = i;
							i++;
						});

						this.spinnerService.showAppSpinner(); // Turns on app wide spinner
						forkJoin([this.jobService.putUserJobChannelSequences(data.key, data.userJobChannels), this.jobService.updateUserJobChannels(data.key, data.userJobChannels)])
							.pipe(this.takeUntilChartDisposed(chartState))
							.subscribe((result: any) => {
								this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
								// location.reload(); // Refreshing the page when channels are changed
							});

						this._mergeChannels();
					} else {
						this.gridsterItem.additionalJsonSettings = JSON.stringify(data.userJobChannels);

						this.findUserUnitPeriodChannels().forEach((_) => {
							this._modifyAxisDisplay(chartState, _.unitPeriodChannel.unitPeriodId, _.unitPeriodChannel.channelId);
						});

						this.changeDetectorRef.markForCheck();

						let dashboardItem = this.currentDashboardGridsterItems?.find((di) => di.chartName === this.gridsterItem.chartName);

						if (dashboardItem) dashboardItem.additionalJsonSettings = this.gridsterItem.additionalJsonSettings;

						this.dashboardService.dashboardSaveDisplay(true);
					}
				}
			});
	}

	/**
	 * When swiping on comments, this method will toggle the view of Channel values and de-toggle all other channel values.
	 */
	swipeMapping(jobComment: JobCommentDto) {
		this.sortedJobComments.forEach((sortedJobComment) => {
			if (sortedJobComment.id !== jobComment.id) {
				sortedJobComment.swiped = false;
			}
		});
		jobComment.swiped = !jobComment.swiped;
	}

	filterJobCommentDefaults() {
		let selectedDefaultGroup = this.job.jobCommentReturn.filteredJobCommentDefaultGroups.find((jobCommentDefaultGroup) => jobCommentDefaultGroup.id === this.jobService.jobCommentDefaultGroupId);

		if (selectedDefaultGroup) {
			this.jobCommentDefaults = selectedDefaultGroup.jobCommentDefaults;
		} else {
			this.jobCommentDefaults = [];
		}
	}

	addJobComment(chartState: ChartState, result: JobCommentDefaultDto) {
		let jobCommentDto: JobCommentDto = {
			id: 0,
			jobId: this.job.id,
			comment: '',
			overrideComment: result?.overrideComment,
			commentDate: new Date(),
			jobCommentDefaultId: result?.id,
			jobCommentDefault: result,
			hideInPortal: false,
			replacements: '',
		};

		this.openCommentAddDialog(chartState, jobCommentDto, '', null);
	}

	private findUserUnitPeriodChannels(): UserUnitPeriodChannelDto[] {
		if (!this.gridsterItem?.additionalJsonSettings) return this.jobService.findUserUnitPeriodChannelsForJob(this.job?.key);
		return JSON.parse(this.gridsterItem.additionalJsonSettings);
	}

	private takeUntilChartDisposed(chartState: ChartState) {
		return (source: Observable<any>) => {
			return source.pipe(
				// We must use both takeWhile and takeUntil. Gross!

				// amCharts autoDispose can dispose the chart before chartState.disposed$ emits a value,
				// so we check isDisposed() directly.
				takeWhile(() => chartState.chart.isDisposed() === false),

				// this will unsubscribe when JobChart calls chartState.dispose()
				takeUntil(chartState.disposed$)
			);
		};
	}

	ngOnDestroy() {
		this.chartState$.value?.dispose();

		super.ngOnDestroy();
	}
}

interface IChartTimeRange {
	start?: number;
	end?: number;
}

interface IDateAxisEvent {
	type?: string;
	target?: am4charts.DateAxis<am4charts.AxisRenderer>;
}

export interface ITimeline2Data {
	time: string;
	text: string;
	icon?: string;
	attachment?: string;
}

export class ChartState {
	constructor(
		public chart: XYChart,
		private zone: NgZone
	) {}

	get chartUid(): string {
		return this.chart.uid;
	}

	readonly disposed = new Subject<void>();
	readonly disposed$ = this.disposed.asObservable();
	series: ChartSeries = {};

	dispose() {
		if (!this.chart.isDisposed()) {
			this.zone.runOutsideAngular(() => {
				this.chart.dispose();
			});
		}

		this.disposed.next();
		this.disposed.complete();
	}
}
